"use client";

import {
  AlertCircle,
  ArrowUpDown,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  Copy,
  Edit3,
  ExternalLink,
  Flame,
  FolderTree,
  Globe,
  ImageOff,
  Layers,
  Loader2,
  Megaphone,
  Package,
  Palette,
  Percent,
  Phone,
  PhoneCall,
  Plus,
  RefreshCw,
  Search,
  SlidersHorizontal,
  Sparkles,
  Store,
  Tag,
  Trash2,
  Upload,
  X,
  XCircle,
  Zap,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AdminPagination } from "@/components/admin/admin-pagination";
import { cn, formatPrice } from "@/lib/utils";
import { getCategoryPermanentLink } from "@/lib/categories";
import type { Product, ProductVariant } from "@/types";

export type VariantForm = {
  id: string;
  color: string;
  colorCode: string;
  size?: string;
  sku: string;
  price: string;
  stock: string;
  image: string;
  images?: string[];
  attributes?: Record<string, string>;
  isActive: boolean;
  isHot?: boolean;
};

export const COLOR_PRESETS = [
  { name: "Space Black", code: "#1a1a1a" },
  { name: "Glacier Silver", code: "#d1d5db" },
  { name: "Midnight Blue", code: "#1e3a8a" },
  { name: "Forest Green", code: "#14532d" },
  { name: "Rose Gold", code: "#b76e79" },
  { name: "Sunset Red", code: "#dc2626" },
  { name: "Titanium Gray", code: "#4b5563" },
  { name: "Pure White", code: "#ffffff" },
  { name: "Beige", code: "#f5f5dc" },
];

export const SIZE_PRESETS = ["XS", "S", "M", "L", "XL", "2XL", "3XL", "Free Size"];

function slugify(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

const EMPTY_FORM = {
  title: "",
  slug: "",
  brand: "",
  description: "",
  image: "",
  price: "",
  mrp: "",
  discount: "0",
  stock: "20",
  tags: "",
  isFeatured: false,
  isPrime: true,
  isSponsored: false,
  isFlashDeal: false,
  inPromotionBanner: false,
  status: "APPROVED" as "APPROVED" | "DRAFT",
};

type FormState = typeof EMPTY_FORM;

export const FORM_STEPS = [
  {
    step: 1,
    id: "general",
    label: "General Information",
    shortLabel: "General Info",
    icon: Package,
  },
  {
    step: 2,
    id: "category",
    label: "Category & Navigation Hierarchy",
    shortLabel: "Hierarchy",
    icon: FolderTree,
  },
  {
    step: 3,
    id: "pricing",
    label: "Pricing & Stock",
    shortLabel: "Pricing",
    icon: Tag,
  },
  {
    step: 4,
    id: "media",
    label: "Media & Gallery Assets",
    shortLabel: "Media",
    icon: Upload,
  },
  {
    step: 5,
    id: "variants",
    label: "Variants (Colors & Sizes)",
    shortLabel: "Variants",
    icon: Palette,
  },
  {
    step: 6,
    id: "merchandising",
    label: "Storefront Merchandising",
    shortLabel: "Merchandising",
    icon: Megaphone,
  },
] as const;

export const DEFAULT_CATEGORY_TREE: Record<string, string[]> = {
  "Electronics": [
    "AirPods & Earbuds",
    "Headphones & Audio",
    "Mobiles & Accessories",
    "Laptops & Computers",
    "Computer Accessories & Stands",
    "TVs & Home Entertainment",
    "Cameras & Photography",
    "Smartwatches & Wearables",
    "Gaming & Consoles",
    "Cables & Power Banks",
  ],
  "Fashion": [
    "Men's Clothing",
    "Women's Clothing",
    "Shoes & Footwear",
    "Bags & Luggage",
    "Watches & Accessories",
    "Jewelry",
  ],
  "Home & Kitchen": [
    "Kitchen & Dining",
    "Home Appliances",
    "Home Decor",
    "Bedding & Linens",
    "Furniture",
    "Storage & Organization",
  ],
  "Beauty & Personal Care": [
    "Skin Care",
    "Hair Care",
    "Fragrances & Perfumes",
    "Makeup",
    "Personal Grooming",
  ],
  "Sports & Outdoors": [
    "Exercise & Fitness",
    "Outdoor Recreation",
    "Sports Apparel",
    "Camping & Hiking",
  ],
  "Office & Stationery": [
    "Office Supplies",
    "Desk Organization",
    "Printers & Ink",
    "Stationery & Notebooks",
  ],
  "Automotive & Tools": [
    "Car Electronics & Audio",
    "Car Accessories",
    "Hand & Power Tools",
  ],
  "Toys & Baby": [
    "Baby Essentials",
    "Toys & Games",
    "Kids' Fashion",
  ],
  "Books & Media": [
    "Fiction & Literature",
    "Non-Fiction",
    "Academic & Textbooks",
  ],
};

export type CategoryHierarchy = {
  main: string;
  sub: string;
  isCustomMain: boolean;
  isCustomSub: boolean;
  extras: string[];
};

export function resolveProductCategoryHierarchy(
  categoriesList: string[] | undefined,
  primaryCategory: string | undefined,
  tree: Record<string, string[]>
): CategoryHierarchy {
  const all = Array.from(
    new Set(
      [...(categoriesList || []), primaryCategory || ""]
        .map((s) => s?.trim())
        .filter((s): s is string => Boolean(s))
    )
  );

  const mainKeys = Object.keys(tree);

  if (all.length === 0) {
    const firstMain = mainKeys[0] || "Electronics";
    const firstSub = (tree[firstMain] && tree[firstMain][0]) || "AirPods & Earbuds";
    return {
      main: firstMain,
      sub: firstSub,
      isCustomMain: false,
      isCustomSub: false,
      extras: [],
    };
  }

  let matchedMain = "";
  let matchedSub = "";
  const remaining: string[] = [];

  for (const cat of all) {
    const lower = cat.toLowerCase();

    // Check if matches a main category directly
    const foundMain = mainKeys.find(
      (m) => m.toLowerCase() === lower || lower.includes(m.toLowerCase())
    );
    if (foundMain && !matchedMain) {
      matchedMain = foundMain;
      continue;
    }

    // Check if matches a subcategory
    let foundSubParent = "";
    let foundSubName = "";
    for (const [m, subs] of Object.entries(tree)) {
      const sub = subs.find(
        (s) =>
          s.toLowerCase() === lower ||
          lower.includes(s.toLowerCase()) ||
          s.toLowerCase().includes(lower)
      );
      if (sub) {
        foundSubParent = m;
        foundSubName = sub;
        break;
      }
    }

    if (foundSubName && !matchedSub) {
      if (!matchedMain) matchedMain = foundSubParent;
      matchedSub = foundSubName;
      continue;
    }

    remaining.push(cat);
  }

  // If no main matched from tree
  if (!matchedMain) {
    if (all.length >= 2) {
      return {
        main: all[0],
        sub: all[1],
        isCustomMain: !mainKeys.includes(all[0]),
        isCustomSub: true,
        extras: all.slice(2),
      };
    }
    return {
      main: all[0],
      sub: "General",
      isCustomMain: !mainKeys.includes(all[0]),
      isCustomSub: true,
      extras: [],
    };
  }

  // If main matched, but no sub matched
  if (!matchedSub) {
    if (remaining.length > 0) {
      matchedSub = remaining[0];
      return {
        main: matchedMain,
        sub: matchedSub,
        isCustomMain: false,
        isCustomSub: !(tree[matchedMain] || []).includes(matchedSub),
        extras: remaining.slice(1),
      };
    }
    const defaultSub = (tree[matchedMain] && tree[matchedMain][0]) || "General";
    return {
      main: matchedMain,
      sub: defaultSub,
      isCustomMain: false,
      isCustomSub: false,
      extras: [],
    };
  }

  return {
    main: matchedMain,
    sub: matchedSub,
    isCustomMain: false,
    isCustomSub: false,
    extras: remaining,
  };
}

let categoriesCache: string[] | null = null;
let rawCategoriesCache: { id: string; name: string; slug: string; parentId?: string | null }[] | null = null;

export function ProductManager({ initialProducts }: { initialProducts: Product[] }) {
  const [products, setProducts] = useState(initialProducts);
  useEffect(() => setProducts(initialProducts), [initialProducts]);
  const [editing, setEditing] = useState<Product | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [additionalImages, setAdditionalImages] = useState<string[]>([]);
  const [newSubUrl, setNewSubUrl] = useState("");
  const [editingSubUrlIdx, setEditingSubUrlIdx] = useState<number | null>(null);
  const [editingSubUrlText, setEditingSubUrlText] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("All");

  // Category Hierarchy (Main Category & Sub Category)
  const [rawCategories, setRawCategories] = useState<
    { id: string; name: string; slug: string; parentId?: string | null }[]
  >(() => rawCategoriesCache || []);
  const [mainCategory, setMainCategory] = useState<string>("");
  const [subCategory, setSubCategory] = useState<string>("");
  const [isCustomMain, setIsCustomMain] = useState(false);
  const [customMainInput, setCustomMainInput] = useState("");
  const [isCustomSub, setIsCustomSub] = useState(false);
  const [customSubInput, setCustomSubInput] = useState("");
  const [extraCategories, setExtraCategories] = useState<string[]>([]);
  const [newExtraCategoryInput, setNewExtraCategoryInput] = useState("");
  const [showExtraOptions, setShowExtraOptions] = useState(false);
  const [filterPlacement, setFilterPlacement] = useState("All");
  const [updatingBannerId, setUpdatingBannerId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const formRef = useRef<HTMLFormElement>(null);

  // Form color & size variants state
  const [variants, setVariants] = useState<VariantForm[]>([]);
  const [newVarColor, setNewVarColor] = useState("");
  const [newVarColorCode, setNewVarColorCode] = useState("#1a1a1a");
  const [newVarSize, setNewVarSize] = useState("");
  const [selectedBatchSizes, setSelectedBatchSizes] = useState<string[]>([]);
  const [newVarSku, setNewVarSku] = useState("");
  const [newVarPrice, setNewVarPrice] = useState("");
  const [newVarStock, setNewVarStock] = useState("");
  const [newVarImage, setNewVarImage] = useState("");

  // Collapsible state for variants section and individual variant cards
  const [isVariantsOpen, setIsVariantsOpen] = useState(false);
  const [collapsedVariantCards, setCollapsedVariantCards] = useState<Record<string, boolean>>({});
  const [modalCollapsedCards, setModalCollapsedCards] = useState<Record<string, boolean>>({});

  function toggleVariantCard(id: string) {
    setCollapsedVariantCards((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }

  function toggleAllVariantCards() {
    const isAllCollapsed =
      variants.length > 0 && variants.every((v) => Boolean(collapsedVariantCards[v.id]));
    if (isAllCollapsed) {
      setCollapsedVariantCards({});
    } else {
      const next: Record<string, boolean> = {};
      variants.forEach((v) => {
        next[v.id] = true;
      });
      setCollapsedVariantCards(next);
    }
  }

  function toggleModalCard(id: string) {
    setModalCollapsedCards((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }

  function toggleAllModalCards() {
    const isAllCollapsed =
      modalVariants.length > 0 && modalVariants.every((v) => Boolean(modalCollapsedCards[v.id]));
    if (isAllCollapsed) {
      setModalCollapsedCards({});
    } else {
      const next: Record<string, boolean> = {};
      modalVariants.forEach((v) => {
        next[v.id] = true;
      });
      setModalCollapsedCards(next);
    }
  }

  // Modal state for quick table variant management
  const [variantModalProduct, setVariantModalProduct] = useState<Product | null>(null);
  const [modalVariants, setModalVariants] = useState<VariantForm[]>([]);
  const [savingModalVariants, setSavingModalVariants] = useState(false);
  const [modalNewColor, setModalNewColor] = useState("");
  const [modalNewColorCode, setModalNewColorCode] = useState("#1a1a1a");
  const [modalNewSize, setModalNewSize] = useState("");
  const [modalSelectedBatchSizes, setModalSelectedBatchSizes] = useState<string[]>([]);
  const [modalNewSku, setModalNewSku] = useState("");
  const [modalNewPrice, setModalNewPrice] = useState("");
  const [modalNewStock, setModalNewStock] = useState("");
  const [modalNewImage, setModalNewImage] = useState("");

  // Quick 1-click toggle for promotion banner placement from the table
  async function togglePromotionBanner(product: Product) {
    const nextState = !product.inPromotionBanner;
    setUpdatingBannerId(product.id);
    try {
      const res = await fetch(`/api/admin/products/${product.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ inPromotionBanner: nextState }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(data?.message || "Failed to update promotion banner setting");
      }
      setProducts((items) =>
        items.map((item) =>
          item.id === product.id ? { ...item, inPromotionBanner: nextState } : item
        )
      );
      toast.success(
        nextState
          ? `"${product.title}" featured in homepage promotion banner!`
          : `"${product.title}" removed from promotion banner.`
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update banner setting");
    } finally {
      setUpdatingBannerId(null);
    }
  }

  const searchParams = useSearchParams();
  const urlStatus = searchParams?.get("status");
  const [filterStatus, setFilterStatus] = useState<string>(
    urlStatus === "PENDING" ? "PENDING" : "All"
  );

  useEffect(() => {
    if (urlStatus === "PENDING") {
      setFilterStatus("PENDING");
    }
  }, [urlStatus]);
  const [rejectModalProduct, setRejectModalProduct] = useState<Product | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [processingStatusId, setProcessingStatusId] = useState<string | null>(null);

  async function handleApprove(product: Product) {
    setProcessingStatusId(product.id);
    try {
      const res = await fetch(`/api/admin/products/${product.id}/approve`, {
        method: "POST",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.message || "Failed to approve listing");
      }
      setProducts((prev) =>
        prev.map((p) => (p.id === product.id ? { ...p, status: "APPROVED", rejectionReason: null } : p))
      );
      toast.success(`"${product.title}" has been approved and is now live!`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to approve listing");
    } finally {
      setProcessingStatusId(null);
    }
  }

  async function handleReject() {
    if (!rejectModalProduct) return;
    if (!rejectReason.trim()) {
      toast.error("Please enter a rejection reason.");
      return;
    }
    setProcessingStatusId(rejectModalProduct.id);
    try {
      const res = await fetch(`/api/admin/products/${rejectModalProduct.id}/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: rejectReason.trim() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.message || "Failed to reject listing");
      }
      const updatedReason = rejectReason.trim();
      setProducts((prev) =>
        prev.map((p) =>
          p.id === rejectModalProduct.id
            ? { ...p, status: "REJECTED", rejectionReason: updatedReason }
            : p
        )
      );
      toast.success(`Listing has been rejected and the seller notified.`);
      setRejectModalProduct(null);
      setRejectReason("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to reject listing");
    } finally {
      setProcessingStatusId(null);
    }
  }

  // Load categories from DB
  const [dbCategories, setDbCategories] = useState<string[]>(() => categoriesCache || []);
  useEffect(() => {
    let active = true;
    function loadCategories() {
      fetch("/api/admin/categories")
        .then((r) => r.json())
        .then((data) => {
          if (active && Array.isArray(data.categories) && data.categories.length > 0) {
            const names: string[] = data.categories.map((c: { name: string }) => c.name);
            categoriesCache = names;
            rawCategoriesCache = data.categories;
            setDbCategories(names);
            setRawCategories(data.categories);
          }
        })
        .catch(() => {});
    }

    loadCategories();
    window.addEventListener("nav-updated", loadCategories);
    return () => {
      active = false;
      window.removeEventListener("nav-updated", loadCategories);
    };
  }, []);

  const { mergedCategoryTree, backendMainCategories, defaultMainCategories } = useMemo(() => {
    const tree: Record<string, string[]> = {};
    const backendMains: string[] = [];

    // 1. First priority: Use real categories defined in the backend category page (/admin/categories)
    if (rawCategories.length > 0) {
      const idToCategory = new Map(rawCategories.map((c) => [c.id, c]));
      const parentCategories = rawCategories.filter((c) => !c.parentId);
      const childCategories = rawCategories.filter((c) => Boolean(c.parentId));

      // Register all root categories from backend as Main Departments in their exact defined order
      for (const parent of parentCategories) {
        if (!tree[parent.name]) {
          tree[parent.name] = [];
          backendMains.push(parent.name);
        }
      }

      // Add child subcategories to their corresponding parent
      for (const child of childCategories) {
        const parent = child.parentId ? idToCategory.get(child.parentId) : undefined;
        if (parent) {
          if (!tree[parent.name]) {
            tree[parent.name] = [];
            backendMains.push(parent.name);
          }
          if (!tree[parent.name].includes(child.name)) {
            tree[parent.name].push(child.name);
          }
        } else {
          if (!tree[child.name]) {
            tree[child.name] = [];
            backendMains.push(child.name);
          }
        }
      }
    } else {
      // Fallback if rawCategories is not yet populated
      for (const name of dbCategories) {
        if (!tree[name]) {
          tree[name] = [];
          backendMains.push(name);
        }
      }
    }

    // 2. Harvest subcategories used by existing products in each department
    products.forEach((p) => {
      const pMain = p.categories && p.categories[0] ? p.categories[0] : p.category;
      const pSub = p.category;
      if (pMain && tree[pMain] && pSub && pSub !== pMain) {
        if (!tree[pMain].includes(pSub)) {
          tree[pMain].push(pSub);
        }
      }
    });

    // Ensure every backend main department has at least standard or "General" subcategories
    for (const main of backendMains) {
      if (!tree[main] || tree[main].length === 0) {
        const matchDefault = Object.entries(DEFAULT_CATEGORY_TREE).find(
          ([k]) => k.toLowerCase() === main.toLowerCase()
        );
        if (matchDefault) {
          tree[main] = [...matchDefault[1]];
        } else {
          tree[main] = ["General"];
        }
      }
    }

    // 3. Fallback: Include any standard categories from DEFAULT_CATEGORY_TREE that are not yet in the backend
    const defaultMains: string[] = [];
    for (const [main, subs] of Object.entries(DEFAULT_CATEGORY_TREE)) {
      const alreadyExists = backendMains.some(
        (m) => m.toLowerCase() === main.toLowerCase()
      );
      if (!alreadyExists) {
        tree[main] = [...subs];
        defaultMains.push(main);
      }
    }

    return {
      mergedCategoryTree: tree,
      backendMainCategories: backendMains,
      defaultMainCategories: defaultMains,
    };
  }, [rawCategories, dbCategories, products]);

  const availableSubCategories = useMemo(() => {
    if (!mainCategory || isCustomMain) return [];
    return mergedCategoryTree[mainCategory] || [];
  }, [mainCategory, isCustomMain, mergedCategoryTree]);

  const allFilterCategories = useMemo(() => {
    const set = new Set<string>();
    products.forEach((p) => {
      if (p.category) set.add(p.category);
      if (Array.isArray(p.categories)) {
        p.categories.forEach((c) => set.add(c));
      }
    });
    dbCategories.forEach((c) => set.add(c));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [products, dbCategories]);

  function handleMainCategoryChange(value: string) {
    if (value === "__custom__") {
      setIsCustomMain(true);
      setCustomMainInput("");
      setMainCategory("");
      setIsCustomSub(true);
      setCustomSubInput("");
      setSubCategory("");
      return;
    }

    setIsCustomMain(false);
    setCustomMainInput("");
    setMainCategory(value);

    const subs = mergedCategoryTree[value] || [];
    setIsCustomSub(false);
    setCustomSubInput("");
    if (subs.length > 0) {
      setSubCategory(subs[0]);
    } else {
      setSubCategory("General");
    }

    setErrors((prev) => {
      const next = { ...prev };
      delete next.mainCategory;
      delete next.subCategory;
      delete next.categories;
      return next;
    });
  }

  function handleSubCategoryChange(value: string) {
    if (value === "__custom__") {
      setIsCustomSub(true);
      setCustomSubInput("");
      setSubCategory("");
      return;
    }

    setIsCustomSub(false);
    setCustomSubInput("");
    setSubCategory(value);

    setErrors((prev) => {
      const next = { ...prev };
      delete next.subCategory;
      delete next.categories;
      return next;
    });
  }

  function cancelCustomMain() {
    setIsCustomMain(false);
    setCustomMainInput("");
    const defaultMain =
      backendMainCategories[0] || Object.keys(mergedCategoryTree)[0] || "Electronics";
    setMainCategory(defaultMain);
    const subs = mergedCategoryTree[defaultMain] || [];
    setSubCategory(subs[0] || "General");
    setIsCustomSub(false);
    setCustomSubInput("");
  }

  function cancelCustomSub() {
    setIsCustomSub(false);
    setCustomSubInput("");
    const subs = availableSubCategories;
    setSubCategory(subs[0] || "General");
  }

  function handleAddExtraCategory() {
    const val = newExtraCategoryInput.trim();
    if (!val) return;
    const effectiveMain = isCustomMain ? customMainInput.trim() : mainCategory.trim();
    const effectiveSub = isCustomSub ? customSubInput.trim() : subCategory.trim();
    if (
      val.toLowerCase() === effectiveMain.toLowerCase() ||
      val.toLowerCase() === effectiveSub.toLowerCase() ||
      extraCategories.some((c) => c.toLowerCase() === val.toLowerCase())
    ) {
      toast.info(`"${val}" is already included.`);
      setNewExtraCategoryInput("");
      return;
    }
    setExtraCategories((prev) => [...prev, val]);
    setNewExtraCategoryInput("");
  }

  function handleRemoveExtraCategory(name: string) {
    setExtraCategories((prev) => prev.filter((c) => c !== name));
  }

  function startEdit(product: Product) {
    setEditing(product);
    setErrors({});
    setImageError(false);

    const hierarchy = resolveProductCategoryHierarchy(
      product.categories,
      product.category,
      mergedCategoryTree
    );
    setMainCategory(hierarchy.main);
    setSubCategory(hierarchy.sub);
    setIsCustomMain(hierarchy.isCustomMain);
    setCustomMainInput(hierarchy.isCustomMain ? hierarchy.main : "");
    setIsCustomSub(hierarchy.isCustomSub);
    setCustomSubInput(hierarchy.isCustomSub ? hierarchy.sub : "");
    setExtraCategories(hierarchy.extras);
    setShowExtraOptions(hierarchy.extras.length > 0);

    setForm({
      title: product.title,
      slug: product.slug,
      brand: product.brand,
      description: product.description,
      image: product.images[0] ?? "",
      price: String(product.price),
      mrp: String(product.mrp),
      discount: String(product.discount),
      stock: String(product.stock),
      tags: product.tags.join(", "),
      isFeatured: product.isFeatured ?? false,
      isPrime: product.isPrime ?? true,
      isSponsored: product.isSponsored ?? false,
      isFlashDeal: product.isFlashDeal ?? false,
      inPromotionBanner: product.inPromotionBanner ?? false,
      status: (product.status === "DRAFT" ? "DRAFT" : "APPROVED") as "APPROVED" | "DRAFT",
    });
    setAdditionalImages(product.images.slice(1));
    setVariants(
      (product.variants || []).map((v) => ({
        id: v.id || `var-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        color: v.color || v.title || "",
        colorCode: v.colorCode || "#1a1a1a",
        size: v.size || v.attributes?.Size || v.attributes?.size || "",
        sku: v.sku || "",
        price: String(v.price || product.price || ""),
        stock: String(v.stock !== undefined ? v.stock : product.stock || "20"),
        image: v.image || "",
        images: v.images || [],
        attributes: v.attributes || {},
        isActive: v.isActive !== false,
        isHot: Boolean(v.isHot),
      }))
    );
    setNewVarColor("");
    setNewVarSize("");
    setSelectedBatchSizes([]);
    setNewVarSku("");
    setNewVarPrice(String(product.price || ""));
    setNewVarStock(String(product.stock || "20"));
    setNewVarImage(product.images[0] || "");
    setIsFormOpen(true);
    setCurrentStep(1);
    setIsVariantsOpen(true);
    // Scroll smoothly to form
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }

  function cancelEdit() {
    setIsFormOpen(false);
    setCurrentStep(1);
    setEditing(null);
    setForm(EMPTY_FORM);
    setMainCategory("");
    setSubCategory("");
    setIsCustomMain(false);
    setCustomMainInput("");
    setIsCustomSub(false);
    setCustomSubInput("");
    setExtraCategories([]);
    setNewExtraCategoryInput("");
    setShowExtraOptions(false);
    setAdditionalImages([]);
    setNewSubUrl("");
    setEditingSubUrlIdx(null);
    setEditingSubUrlText("");
    setVariants([]);
    setNewVarColor("");
    setNewVarColorCode("#1a1a1a");
    setNewVarSize("");
    setSelectedBatchSizes([]);
    setNewVarSku("");
    setNewVarPrice("");
    setNewVarStock("");
    setNewVarImage("");
    setErrors({});
    setImageError(false);
  }

  function handleToggleCreate() {
    if (isFormOpen && !editing) {
      setIsFormOpen(false);
    } else {
      if (editing) {
        cancelEdit();
      }
      setIsFormOpen(true);
      setCurrentStep(1);
      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    }
  }

  function getStepHasError(step: number): boolean {
    if (step === 1) return Boolean(errors.title || errors.brand || errors.description);
    if (step === 2) return Boolean(errors.mainCategory || errors.subCategory || errors.categories);
    if (step === 3) return Boolean(errors.price || errors.mrp || errors.discount || errors.stock);
    return false;
  }

  function handleNextStep() {
    if (currentStep === 1) {
      const e: Record<string, string> = {};
      if (!form.title.trim() || form.title.trim().length < 3) e.title = "At least 3 characters";
      if (!form.brand.trim() || form.brand.trim().length < 2) e.brand = "At least 2 characters";
      if (!form.description.trim() || form.description.trim().length < 12)
        e.description = "At least 12 characters";
      if (Object.keys(e).length > 0) {
        setErrors((prev) => ({ ...prev, ...e }));
        toast.error("Please fill in required general information before continuing.");
        return;
      }
    } else if (currentStep === 2) {
      const effectiveMain = isCustomMain ? customMainInput.trim() : mainCategory.trim();
      const effectiveSub = isCustomSub ? customSubInput.trim() : subCategory.trim();
      const e: Record<string, string> = {};
      if (!effectiveMain) e.mainCategory = "Please select or enter a main category";
      if (!effectiveSub) e.subCategory = "Please select or enter a sub category";
      if (Object.keys(e).length > 0) {
        setErrors((prev) => ({ ...prev, ...e }));
        toast.error("Please select or enter department and subcategory.");
        return;
      }
    } else if (currentStep === 3) {
      const e: Record<string, string> = {};
      if (!form.price || Number(form.price) <= 0) e.price = "Must be a positive number";
      if (form.mrp && Number(form.mrp) <= 0) e.mrp = "Must be a positive number";
      if (Number(form.discount) < 0 || Number(form.discount) > 95)
        e.discount = "Between 0 and 95";
      if (!form.stock || Number(form.stock) < 0) e.stock = "Cannot be negative";
      if (Object.keys(e).length > 0) {
        setErrors((prev) => ({ ...prev, ...e }));
        toast.error("Please enter valid pricing and stock values.");
        return;
      }
    }

    if (currentStep < FORM_STEPS.length) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      if (nextStep === 5) {
        setIsVariantsOpen(true);
      }
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  function handlePrevStep() {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  function updateVariant(id: string, patch: Partial<VariantForm>) {
    setVariants((prev) => prev.map((v) => (v.id === id ? { ...v, ...patch } : v)));
  }

  function removeVariant(id: string) {
    setVariants((prev) => prev.filter((v) => v.id !== id));
  }

  function handleAddVariant() {
    const color = newVarColor.trim();
    if (!color) {
      toast.error("Please enter a color name (e.g. Black, White, Beige)");
      return;
    }
    const baseSlug = form.slug || slugify(form.title) || "prod";
    const price = newVarPrice.trim() || form.price || "";
    const stock = newVarStock.trim() || form.stock || "20";
    const image = newVarImage.trim() || form.image.trim() || "";

    const sizesToCreate =
      selectedBatchSizes.length > 0
        ? selectedBatchSizes
        : newVarSize.trim()
        ? [newVarSize.trim()]
        : [""];

    const newVariants: VariantForm[] = sizesToCreate.map((sizeVal) => {
      const id = `var-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      const sizeSuffix = sizeVal ? `-${sizeVal.toLowerCase().replace(/[^a-z0-9]+/g, "-")}` : "";
      const sku =
        (sizesToCreate.length === 1 && newVarSku.trim() ? newVarSku.trim() : "") ||
        `${baseSlug}-${color.toLowerCase().replace(/[^a-z0-9]+/g, "-")}${sizeSuffix}`.toUpperCase();

      return {
        id,
        color,
        colorCode: newVarColorCode || "#1a1a1a",
        size: sizeVal || undefined,
        sku,
        price,
        stock,
        image,
        isActive: true,
      };
    });

    setVariants((prev) => [...prev, ...newVariants]);
    setIsVariantsOpen(true);
    newVariants.forEach((v) => {
      setCollapsedVariantCards((prev) => ({ ...prev, [v.id]: false }));
    });

    setNewVarColor("");
    setNewVarSize("");
    setSelectedBatchSizes([]);
    setNewVarSku("");
    setNewVarPrice(form.price || "");
    setNewVarStock(form.stock || "20");
    setNewVarImage("");

    if (sizesToCreate.length > 1) {
      toast.success(
        `Added ${sizesToCreate.length} variants for "${color}" (${sizesToCreate.join(", ")})`
      );
    } else {
      toast.success(
        `Variant "${color}${sizesToCreate[0] ? ` (${sizesToCreate[0]})` : ""}" added`
      );
    }
  }

  function openVariantModal(product: Product) {
    setVariantModalProduct(product);
    setModalVariants(
      (product.variants || []).map((v) => ({
        id: v.id || `var-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        color: v.color || v.title || "",
        colorCode: v.colorCode || "#1a1a1a",
        size: v.size || v.attributes?.Size || v.attributes?.size || "",
        sku: v.sku || "",
        price: String(v.price || product.price || ""),
        stock: String(v.stock !== undefined ? v.stock : product.stock || "20"),
        image: v.image || "",
        images: v.images || [],
        attributes: v.attributes || {},
        isActive: v.isActive !== false,
        isHot: Boolean(v.isHot),
      }))
    );
    setModalNewColor("");
    setModalNewColorCode("#1a1a1a");
    setModalNewSize("");
    setModalSelectedBatchSizes([]);
    setModalNewSku("");
    setModalNewPrice(String(product.price));
    setModalNewStock(String(product.stock));
    setModalNewImage(product.images[0] || "");
  }

  function updateModalVariant(id: string, patch: Partial<VariantForm>) {
    setModalVariants((prev) => prev.map((v) => (v.id === id ? { ...v, ...patch } : v)));
  }

  function removeModalVariant(id: string) {
    setModalVariants((prev) => prev.filter((v) => v.id !== id));
  }

  function handleModalAddVariant() {
    const color = modalNewColor.trim();
    if (!color) {
      toast.error("Please enter a color name");
      return;
    }
    const baseSlug = variantModalProduct?.slug || "prod";
    const price = modalNewPrice.trim() || String(variantModalProduct?.price || "");
    const stock = modalNewStock.trim() || String(variantModalProduct?.stock || "20");
    const image = modalNewImage.trim() || variantModalProduct?.images[0] || "";

    const sizesToCreate =
      modalSelectedBatchSizes.length > 0
        ? modalSelectedBatchSizes
        : modalNewSize.trim()
        ? [modalNewSize.trim()]
        : [""];

    const newVariants: VariantForm[] = sizesToCreate.map((sizeVal) => {
      const id = `var-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      const sizeSuffix = sizeVal ? `-${sizeVal.toLowerCase().replace(/[^a-z0-9]+/g, "-")}` : "";
      const sku =
        (sizesToCreate.length === 1 && modalNewSku.trim() ? modalNewSku.trim() : "") ||
        `${baseSlug}-${color.toLowerCase().replace(/[^a-z0-9]+/g, "-")}${sizeSuffix}`.toUpperCase();

      return {
        id,
        color,
        colorCode: modalNewColorCode || "#1a1a1a",
        size: sizeVal || undefined,
        sku,
        price,
        stock,
        image,
        isActive: true,
      };
    });

    setModalVariants((prev) => [...prev, ...newVariants]);
    setModalNewColor("");
    setModalNewSize("");
    setModalSelectedBatchSizes([]);
    setModalNewSku("");
    setModalNewPrice(String(variantModalProduct?.price || ""));
    setModalNewStock(String(variantModalProduct?.stock || "20"));
    setModalNewImage(variantModalProduct?.images[0] || "");

    if (sizesToCreate.length > 1) {
      toast.success(
        `Added ${sizesToCreate.length} variants for "${color}" (${sizesToCreate.join(", ")})`
      );
    } else {
      toast.success(
        `Variant "${color}${sizesToCreate[0] ? ` (${sizesToCreate[0]})` : ""}" added`
      );
    }
  }

  async function saveModalVariants() {
    if (!variantModalProduct) return;
    setSavingModalVariants(true);
    try {
      const compiled: ProductVariant[] = modalVariants
        .filter((v) => v.color.trim())
        .map((v) => {
          const vPrice = Number(v.price) > 0 ? Number(v.price) : variantModalProduct.price;
          const vStock =
            v.stock !== "" && !isNaN(Number(v.stock))
              ? Number(v.stock)
              : variantModalProduct.stock;
          const vSize = v.size?.trim();
          const attributes: Record<string, string> = { ...(v.attributes || {}) };
          if (vSize) {
            attributes["Size"] = vSize;
          }
          const colorName = v.color.trim();
          const title = vSize ? `${colorName} - ${vSize}` : colorName;

          return {
            id: v.id,
            title,
            color: colorName,
            colorCode: v.colorCode || "#000000",
            sku:
              v.sku.trim() ||
              `${variantModalProduct.slug}-${colorName.toLowerCase().replace(/[^a-z0-9]+/g, "-")}${
                vSize ? `-${vSize.toLowerCase().replace(/[^a-z0-9]+/g, "-")}` : ""
              }`.toUpperCase(),
            price: vPrice,
            stock: vStock,
            image: v.image.trim() || variantModalProduct.images[0] || "",
            images: v.images && v.images.length > 0 ? v.images : undefined,
            attributes: Object.keys(attributes).length > 0 ? attributes : undefined,
            isActive: v.isActive,
            isHot: Boolean(v.isHot),
          };
        });

      const res = await fetch(`/api/admin/products/${variantModalProduct.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ variants: compiled }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.message || "Failed to save variants");

      const saved: Product = data.product;
      setProducts((prev) => prev.map((p) => (p.id === saved.id ? saved : p)));
      if (editing?.id === saved.id) {
        setEditing(saved);
        setVariants(modalVariants);
      }
      toast.success(
        `Saved ${compiled.length} color variant(s) for "${variantModalProduct.title}"`
      );
      setVariantModalProduct(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save variants");
    } finally {
      setSavingModalVariants(false);
    }
  }

  function set(key: keyof FormState, value: string | boolean) {
    setForm((f) => ({ ...f, [key]: value }));
    if (key === "title" && !editing && typeof value === "string") {
      setForm((f) => ({ ...f, title: value, slug: slugify(value) }));
    }
    if (errors[key]) {
      setErrors((e) => {
        const next = { ...e };
        delete next[key];
        return next;
      });
    }
  }

  function handleAddSubUrl() {
    const url = newSubUrl.trim();
    if (!url) return;
    if (additionalImages.includes(url) || url === form.image.trim()) {
      toast.error("This image URL is already in the gallery.");
      return;
    }
    setAdditionalImages((prev) => [...prev, url]);
    setNewSubUrl("");
  }

  function handleRemoveSubUrl(index: number) {
    if (editingSubUrlIdx === index) {
      setEditingSubUrlIdx(null);
      setEditingSubUrlText("");
    }
    setAdditionalImages((prev) => prev.filter((_, i) => i !== index));
  }

  function handleStartEditSubUrl(index: number, currentUrl: string) {
    setEditingSubUrlIdx(index);
    setEditingSubUrlText(currentUrl);
  }

  function handleSaveEditSubUrl(index: number) {
    const trimmed = editingSubUrlText.trim();
    if (!trimmed) {
      toast.error("Image URL cannot be empty");
      return;
    }
    const isDuplicate =
      additionalImages.some((u, i) => i !== index && u === trimmed) ||
      trimmed === form.image.trim();
    if (isDuplicate) {
      toast.error("This image URL is already in the gallery.");
      return;
    }
    setAdditionalImages((prev) => {
      const next = [...prev];
      next[index] = trimmed;
      return next;
    });
    setEditingSubUrlIdx(null);
    setEditingSubUrlText("");
    toast.success("Sub URL updated");
  }

  function handleCancelEditSubUrl() {
    setEditingSubUrlIdx(null);
    setEditingSubUrlText("");
  }

  function handleMoveSubUrl(fromIndex: number, toIndex: number) {
    if (toIndex < 0 || toIndex >= additionalImages.length) return;
    setAdditionalImages((prev) => {
      const next = [...prev];
      const [item] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, item);
      return next;
    });
  }

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!form.title.trim() || form.title.trim().length < 2) e.title = "At least 2 characters";

    const effectiveMain = isCustomMain ? customMainInput.trim() : mainCategory.trim();
    const effectiveSub = isCustomSub ? customSubInput.trim() : subCategory.trim();
    if (!effectiveMain && !effectiveSub) e.mainCategory = "Please select or enter a category";

    if (!form.price || Number(form.price) <= 0) e.price = "Must be a positive number";
    if (form.mrp && Number(form.mrp) <= 0) e.mrp = "Must be a positive number";
    if (Number(form.discount) < 0 || Number(form.discount) > 95)
      e.discount = "Between 0 and 95";
    if (!form.stock || Number(form.stock) < 0) e.stock = "Cannot be negative";

    setErrors(e);

    if (Object.keys(e).length > 0) {
      if (e.title || e.brand || e.description) {
        setCurrentStep(1);
      } else if (e.mainCategory || e.subCategory) {
        setCurrentStep(2);
      } else if (e.price || e.mrp || e.discount || e.stock) {
        setCurrentStep(3);
      }
      return false;
    }
    return true;
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!validate()) {
      toast.error("Fix the highlighted fields before saving.");
      return;
    }
    setLoading(true);

    let slug = (form.slug.trim() || slugify(form.title)).slice(0, 180).replace(/-+$/, "");
    if (!slug || slug.length < 3) {
      slug = `admin-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
    }

    // Combine main image + additional gallery sub-urls + variant images
    const variantImages = variants
      .map((v) => v.image?.trim())
      .filter(Boolean) as string[];
    const rawImages = Array.from(
      new Set(
        [
          form.image.trim(),
          ...additionalImages.map((img) => img.trim()),
          ...variantImages,
        ].filter(Boolean)
      )
    );
    const allImages = rawImages.length > 0
      ? rawImages
      : ["https://images.unsplash.com/photo-1523275335684-37898b6baf30"];

    const compiledVariants: ProductVariant[] = variants
      .filter((v) => v.color.trim())
      .map((v) => {
        const vPrice = Number(v.price) > 0 ? Number(v.price) : Number(form.price) || 0;
        const vStock =
          v.stock !== "" && !isNaN(Number(v.stock)) ? Number(v.stock) : Number(form.stock) || 0;
        const vSize = v.size?.trim();
        const attributes: Record<string, string> = { ...(v.attributes || {}) };
        if (vSize) {
          attributes["Size"] = vSize;
        }
        const colorName = v.color.trim();
        const title = vSize ? `${colorName} - ${vSize}` : colorName;

        return {
          id: v.id,
          title,
          color: colorName,
          colorCode: v.colorCode || "#000000",
          sku:
            v.sku.trim() ||
            `${slug}-${colorName.toLowerCase().replace(/[^a-z0-9]+/g, "-")}${
              vSize ? `-${vSize.toLowerCase().replace(/[^a-z0-9]+/g, "-")}` : ""
            }`.toUpperCase(),
          price: vPrice,
          stock: vStock,
          image: v.image.trim() || form.image.trim(),
          images: v.images && v.images.length > 0 ? v.images : undefined,
          attributes: Object.keys(attributes).length > 0 ? attributes : undefined,
          isActive: v.isActive,
          isHot: Boolean(v.isHot),
        };
      });

    const effectiveMain = isCustomMain ? customMainInput.trim() : mainCategory.trim();
    const effectiveSub = isCustomSub ? customSubInput.trim() : subCategory.trim();
    const compiledCategories = Array.from(
      new Set(
        [effectiveMain, effectiveSub, ...extraCategories.map((c) => c.trim())].filter(Boolean)
      )
    );

    const payload = {
      title: form.title.trim(),
      slug,
      brand: form.brand.trim(),
      category: effectiveSub || effectiveMain,
      categories: compiledCategories,
      description: form.description.trim(),
      images: allImages,
      variants: compiledVariants,
      price: Number(form.price),
      mrp: Number(form.mrp) > 0 ? Number(form.mrp) : Number(form.price),
      discount: Number(form.discount),
      stock: Number(form.stock),
      rating: editing?.rating ?? 0,
      reviewCount: editing?.reviewCount ?? 0,
      tags: Array.from(
        new Set(
          form.tags
            .split(",")
            .map((t) => t.trim().toLowerCase())
            .filter(Boolean)
        )
      ),
      isPrime: form.isPrime,
      isFeatured: form.isFeatured,
      isSponsored: form.isSponsored,
      isFlashDeal: form.isFlashDeal,
      inPromotionBanner: form.inPromotionBanner,
      status: form.status || "APPROVED",
    };

    try {
      const url = editing ? `/api/admin/products/${editing.id}` : "/api/admin/products";
      const method = editing ? "PATCH" : "POST";
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.message ?? `Server error ${response.status}`);
      }

      const saved: Product = data.product;
      setProducts((items) =>
        editing
          ? items.map((item) => (item.id === editing.id ? saved : item))
          : [saved, ...items]
      );
      cancelEdit();
      toast.success(editing ? "Product updated" : "Product added", {
        description: `Saved with ${allImages.length} image(s).`,
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to save product.");
    } finally {
      setLoading(false);
    }
  }

  async function deleteProduct(id: string) {
    const previous = products;
    setProducts((items) => items.filter((item) => item.id !== id));
    setConfirmDeleteId(null);

    try {
      const response = await fetch(`/api/admin/products/${id}`, { method: "DELETE" });
      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.message ?? "Unable to delete product.");
      }
      toast.success("Product removed");
    } catch (error) {
      setProducts(previous);
      toast.error(error instanceof Error ? error.message : "Unable to delete product.");
    }
  }

  const pendingCount = useMemo(
    () => products.filter((p) => p.status === "PENDING" || p.status === "PENDING_REVIEW").length,
    [products]
  );
  const approvedCount = useMemo(
    () => products.filter((p) => p.status === "APPROVED" || !p.status).length,
    [products]
  );
  const rejectedCount = useMemo(
    () => products.filter((p) => p.status === "REJECTED").length,
    [products]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((p) => {
      const matchesSearch =
        !q ||
        p.title.toLowerCase().includes(q) ||
        p.brand.toLowerCase().includes(q) ||
        p.slug.toLowerCase().includes(q);
      const productCategories = p.categories?.length ? p.categories : [p.category];
      const matchesCategory =
        filterCategory === "All" || productCategories.includes(filterCategory);

      let matchesPlacement = true;
      if (filterPlacement === "Banner") {
        matchesPlacement = Boolean(p.inPromotionBanner);
      } else if (filterPlacement === "Featured") {
        matchesPlacement = Boolean(p.isFeatured);
      } else if (filterPlacement === "Deals") {
        matchesPlacement = Boolean(p.isFlashDeal);
      } else if (filterPlacement === "Sponsored") {
        matchesPlacement = Boolean(p.isSponsored);
      }

      let matchesStatus = true;
      if (filterStatus === "PENDING") {
        matchesStatus = p.status === "PENDING" || p.status === "PENDING_REVIEW";
      } else if (filterStatus === "APPROVED") {
        matchesStatus = p.status === "APPROVED" || !p.status;
      } else if (filterStatus === "REJECTED") {
        matchesStatus = p.status === "REJECTED";
      }

      return matchesSearch && matchesCategory && matchesPlacement && matchesStatus;
    });
  }, [products, search, filterCategory, filterPlacement, filterStatus]);

  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  const stats = useMemo(
    () => ({
      inventory: products.reduce((sum, p) => sum + p.stock, 0),
      averageDiscount: products.length
        ? Math.round(products.reduce((sum, p) => sum + p.discount, 0) / products.length)
        : 0,
      withVariants: products.filter((p) => p.variants && p.variants.length > 0).length,
      inBanner: products.filter((p) => p.inPromotionBanner).length,
    }),
    [products]
  );

  const inputClass = (field: string) =>
    `h-10 w-full rounded-lg border bg-white px-3 text-sm text-slate-900 transition-all placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amazon-orange/30 dark:bg-slate-900 dark:text-white dark:placeholder:text-slate-500 ${
      errors[field]
        ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
        : "border-slate-200 hover:border-slate-300 focus:border-amazon-orange dark:border-white/10 dark:hover:border-white/20"
    }`;

  const isAnyFilterActive =
    search.trim() !== "" ||
    filterCategory !== "All" ||
    filterPlacement !== "All" ||
    filterStatus !== "All";

  function clearAllFilters() {
    setSearch("");
    setFilterCategory("All");
    setFilterPlacement("All");
    setFilterStatus("All");
    setPage(1);
  }

  return (
    <div className="space-y-6">
      {/* 1. Metric Stats Cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6 sm:gap-4">
        {/* Pending Approval Submissions */}
        <div
          onClick={() => {
            setFilterStatus(filterStatus === "PENDING" ? "All" : "PENDING");
            setPage(1);
          }}
          className={cn(
            "group relative overflow-hidden rounded-2xl border p-4 shadow-xs transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md cursor-pointer",
            pendingCount > 0
              ? "border-amber-400 bg-amber-50/80 dark:border-amber-500/40 dark:bg-amber-950/40"
              : "border-slate-200/90 bg-white dark:border-white/10 dark:bg-slate-900",
            filterStatus === "PENDING" && "ring-2 ring-amber-500"
          )}
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Pending Review
            </span>
            <div
              className={cn(
                "flex h-9 w-9 items-center justify-center rounded-xl transition-colors",
                pendingCount > 0
                  ? "bg-amber-500 text-white shadow-xs"
                  : "bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-slate-400"
              )}
            >
              <Clock className="h-4 w-4" />
            </div>
          </div>
          <p
            className={cn(
              "mt-2 text-2xl font-black tracking-tight sm:text-3xl",
              pendingCount > 0 ? "text-amber-700 dark:text-amber-300" : "text-slate-950 dark:text-white"
            )}
          >
            {pendingCount}
          </p>
          <div className="mt-2 flex items-center gap-1.5 text-xs font-semibold">
            {pendingCount > 0 ? (
              <>
                <span className="inline-block h-2 w-2 rounded-full bg-amber-500 animate-ping" />
                <span className="text-amber-700 dark:text-amber-300">Requires review</span>
              </>
            ) : (
              <>
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
                <span className="text-slate-500 dark:text-slate-400">All reviewed</span>
              </>
            )}
          </div>
        </div>

        {/* Total Products */}
        <div className="group relative overflow-hidden rounded-2xl border border-slate-200/90 bg-white p-4 shadow-xs transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md dark:border-white/10 dark:bg-slate-900">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Total Products
            </span>
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-600 transition-colors group-hover:bg-blue-100 dark:bg-blue-950/60 dark:text-blue-400">
              <Package className="h-4 w-4" />
            </div>
          </div>
          <p className="mt-2 text-2xl font-black tracking-tight text-slate-950 dark:text-white sm:text-3xl">
            {products.length}
          </p>
          <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-blue-500" />
            <span className="truncate">Active live listings</span>
          </div>
        </div>

        {/* Promotion Banner */}
        <div className="group relative overflow-hidden rounded-2xl border border-slate-200/90 bg-white p-4 shadow-xs transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md dark:border-white/10 dark:bg-slate-900">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Hero Banner
            </span>
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-50 text-amber-600 transition-colors group-hover:bg-amber-100 dark:bg-amber-950/60 dark:text-amber-400">
              <Megaphone className="h-4 w-4" />
            </div>
          </div>
          <p className="mt-2 text-2xl font-black tracking-tight text-slate-950 dark:text-white sm:text-3xl">
            {stats.inBanner}
          </p>
          <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 font-medium">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
            <span className="truncate">Featured on storefront</span>
          </div>
        </div>

        {/* Color Variants */}
        <div className="group relative overflow-hidden rounded-2xl border border-slate-200/90 bg-white p-4 shadow-xs transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md dark:border-white/10 dark:bg-slate-900">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Color Variants
            </span>
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-purple-50 text-purple-600 transition-colors group-hover:bg-purple-100 dark:bg-purple-950/60 dark:text-purple-400">
              <Palette className="h-4 w-4" />
            </div>
          </div>
          <p className="mt-2 text-2xl font-black tracking-tight text-slate-950 dark:text-white sm:text-3xl">
            {stats.withVariants}
          </p>
          <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-purple-500" />
            <span className="truncate">
              {products.length ? Math.round((stats.withVariants / products.length) * 100) : 0}% of
              catalog
            </span>
          </div>
        </div>

        {/* Total Inventory */}
        <div className="group relative overflow-hidden rounded-2xl border border-slate-200/90 bg-white p-4 shadow-xs transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md dark:border-white/10 dark:bg-slate-900">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Total Stock
            </span>
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 transition-colors group-hover:bg-emerald-100 dark:bg-emerald-950/60 dark:text-emerald-400">
              <Layers className="h-4 w-4" />
            </div>
          </div>
          <p className="mt-2 text-2xl font-black tracking-tight text-slate-950 dark:text-white sm:text-3xl">
            {stats.inventory.toLocaleString()}
          </p>
          <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
            <span className="truncate">Available inventory</span>
          </div>
        </div>

        {/* Average Discount */}
        <div className="col-span-2 group relative overflow-hidden rounded-2xl border border-slate-200/90 bg-white p-4 shadow-xs transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md dark:border-white/10 dark:bg-slate-900 sm:col-span-1">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Avg Discount
            </span>
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-rose-50 text-rose-600 transition-colors group-hover:bg-rose-100 dark:bg-rose-950/60 dark:text-rose-400">
              <Percent className="h-4 w-4" />
            </div>
          </div>
          <p className="mt-2 text-2xl font-black tracking-tight text-slate-950 dark:text-white sm:text-3xl">
            {stats.averageDiscount}%
          </p>
          <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-rose-500" />
            <span className="truncate">Promotional margin</span>
          </div>
        </div>
      </div>

      {/* 2. Catalog Action Header with "Create New Product" button */}
      <div className="flex flex-col gap-3 rounded-2xl border border-slate-200/90 bg-white p-4 shadow-xs transition-all duration-200 dark:border-white/10 dark:bg-slate-900 sm:flex-row sm:items-center sm:justify-between sm:p-5">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/10 text-amazon-orange dark:bg-amber-500/15">
            <Package className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base font-black tracking-tight text-slate-950 dark:text-white sm:text-lg">
                Catalog Inventory
              </h2>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                {products.length} Products
              </span>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {isFormOpen
                ? editing
                  ? `Editing #${editing.id.slice(0, 8)}: "${editing.title}"`
                  : "Fill in product specifications, media assets, and variants below."
                : "Create new product listings, manage variants, pricing, and storefront promotions."}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            onClick={handleToggleCreate}
            className={cn(
              "h-10 gap-2 rounded-xl text-xs font-bold shadow-sm transition active:scale-[0.98]",
              isFormOpen && !editing
                ? "border border-slate-200 bg-slate-100 text-slate-800 hover:bg-slate-200 dark:border-white/10 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                : "bg-amazon-orange text-slate-950 hover:bg-[#e07320]"
            )}
          >
            {isFormOpen && !editing ? (
              <>
                <X className="h-4 w-4" />
                <span>Close Form</span>
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 stroke-[2.5]" />
                <span>Create New Product</span>
              </>
            )}
          </Button>
        </div>
      </div>

      {/* 3. Product Form (Add New / Edit Product) */}
      {isFormOpen && (
        <form
          ref={formRef}
          onSubmit={onSubmit}
          className={cn(
            "relative overflow-hidden rounded-2xl border bg-white shadow-xs transition-all duration-200 dark:bg-slate-900 animate-in fade-in slide-in-from-top-3 duration-200",
            editing
              ? "border-amazon-orange shadow-amber-500/5 ring-1 ring-amazon-orange/30 dark:border-amazon-orange"
              : "border-slate-200/90 dark:border-white/10"
          )}
        >
          {/* Top Header Bar */}
          <div
            className={cn(
              "flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4 sm:px-6",
              editing
                ? "border-amazon-orange/20 bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent dark:border-amazon-orange/20 dark:from-amber-500/15"
                : "border-slate-100 bg-slate-50/50 dark:border-white/5 dark:bg-slate-800/30"
            )}
          >
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
                  editing
                    ? "bg-amazon-orange text-slate-950 shadow-sm"
                    : "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                )}
              >
                {editing ? <Edit3 className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-black tracking-tight text-slate-950 dark:text-white sm:text-xl">
                    {editing ? "Edit Product" : "Create New Product"}
                  </h2>
                  {editing && (
                    <span className="rounded-full bg-amazon-orange/15 px-2.5 py-0.5 text-[11px] font-bold text-amazon-orange">
                      ID: #{editing.id.slice(0, 8)}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {editing
                    ? `Modifying "${editing.title}". Adjust pricing, stock, categories, and color variants.`
                    : "Configure base product details, classification, media assets, and merchandising slots."}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {editing ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={cancelEdit}
                  className="h-9 gap-1.5 text-xs font-semibold"
                >
                  <X className="h-3.5 w-3.5" />
                  Cancel Edit
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsFormOpen(false)}
                  className="h-9 gap-1.5 text-xs font-semibold"
                >
                  <X className="h-3.5 w-3.5" />
                  Close Form
                </Button>
              )}
              <Button
                type="submit"
                size="sm"
                disabled={loading}
                className="h-9 gap-1.5 bg-amazon-orange text-xs font-bold text-slate-950 hover:bg-[#e07320]"
              >
                {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                {editing ? "Save Changes" : "Create New Product"}
              </Button>
            </div>
          </div>

          {/* Step Wizard Tabs Bar */}
          <div className="border-b border-slate-200/80 bg-slate-50/70 px-4 py-2.5 dark:border-white/10 dark:bg-slate-800/50 sm:px-6">
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none sm:gap-2">
              {FORM_STEPS.map((s) => {
                const Icon = s.icon;
                const isActive = currentStep === s.step;
                const isPast = currentStep > s.step;
                const hasError = getStepHasError(s.step);

                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => {
                      setCurrentStep(s.step);
                      if (s.step === 5) setIsVariantsOpen(true);
                    }}
                    className={cn(
                      "group relative flex shrink-0 items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold transition-all duration-150",
                      isActive
                        ? "bg-white text-slate-950 shadow-xs ring-1 ring-slate-200 dark:bg-slate-900 dark:text-white dark:ring-white/15"
                        : isPast
                        ? "text-slate-600 hover:bg-white/60 dark:text-slate-300 dark:hover:bg-slate-800/70"
                        : "text-slate-400 hover:bg-white/40 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-slate-800/40 dark:hover:text-slate-300"
                    )}
                  >
                    <span
                      className={cn(
                        "flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold transition-colors",
                        hasError
                          ? "bg-red-500 text-white"
                          : isActive
                          ? "bg-amazon-orange text-slate-950 shadow-xs"
                          : isPast
                          ? "bg-emerald-500/15 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400"
                          : "bg-slate-200/80 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
                      )}
                    >
                      {hasError ? "!" : isPast ? <Check className="h-3 w-3 stroke-[3]" /> : s.step}
                    </span>
                    <Icon className={cn("h-3.5 w-3.5", isActive ? "text-amazon-orange" : "text-slate-400")} />
                    <span className="hidden sm:inline">{s.label}</span>
                    <span className="inline sm:hidden">{s.shortLabel}</span>

                    {isActive && (
                      <span className="absolute -bottom-2.5 left-2 right-2 h-0.5 rounded-full bg-amazon-orange" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

        <div className="p-5 sm:p-6 space-y-6">
          {/* Step 1: General Information */}
          {currentStep === 1 && (
            <div className="space-y-4 animate-in fade-in duration-150">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 text-[10px] text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                  1
                </span>
                <span>General Information</span>
              </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {/* Title */}
              <div className="sm:col-span-2">
                <label className="mb-1.5 flex items-center justify-between text-xs font-bold text-slate-700 dark:text-slate-300">
                  <span>
                    Product Title <span className="text-red-500">*</span>
                  </span>
                  <span className="text-[11px] font-normal text-slate-400">
                    Min 3 characters
                  </span>
                </label>
                <input
                  value={form.title}
                  onChange={(e) => set("title", e.target.value)}
                  placeholder="e.g. Samsung Galaxy S24 Ultra 256GB Titanium"
                  className={inputClass("title")}
                />
                {errors.title && <p className="mt-1 text-xs text-red-500">{errors.title}</p>}
              </div>

              {/* Brand */}
              <div>
                <label className="mb-1.5 block text-xs font-bold text-slate-700 dark:text-slate-300">
                  Brand Name <span className="text-red-500">*</span>
                </label>
                <input
                  value={form.brand}
                  onChange={(e) => set("brand", e.target.value)}
                  placeholder="e.g. Samsung, Apple, Nike"
                  className={inputClass("brand")}
                />
                {errors.brand && <p className="mt-1 text-xs text-red-500">{errors.brand}</p>}
              </div>

              {/* Slug */}
              <div className="sm:col-span-2">
                <label className="mb-1.5 flex items-center justify-between text-xs font-bold text-slate-700 dark:text-slate-300">
                  <span>URL Slug</span>
                  <span className="text-[11px] font-normal text-slate-400">
                    Auto-generated from title
                  </span>
                </label>
                <input
                  value={form.slug}
                  onChange={(e) => set("slug", e.target.value)}
                  placeholder="auto-generated-from-title"
                  className={cn(inputClass("slug"), "font-mono text-xs")}
                />
              </div>

              {/* Tags */}
              <div>
                <label className="mb-1.5 flex items-center justify-between text-xs font-bold text-slate-700 dark:text-slate-300">
                  <span>Search Tags</span>
                  <span className="text-[11px] font-normal text-slate-400">
                    Case-insensitive
                  </span>
                </label>
                <input
                  value={form.tags}
                  onChange={(e) => set("tags", e.target.value)}
                  onBlur={() => {
                    if (form.tags.trim()) {
                      const normalized = Array.from(
                        new Set(
                          form.tags
                            .split(",")
                            .map((t) => t.trim().toLowerCase())
                            .filter(Boolean)
                        )
                      ).join(", ");
                      set("tags", normalized);
                    }
                  }}
                  placeholder="smartphone, 5g, flagship"
                  className={inputClass("tags")}
                />
              </div>

              {/* Description */}
              <div className="sm:col-span-2 lg:col-span-3">
                <label className="mb-1.5 flex items-center justify-between text-xs font-bold text-slate-700 dark:text-slate-300">
                  <span>
                    Product Description <span className="text-red-500">*</span>
                  </span>
                  <span className="text-[11px] font-normal text-slate-400">
                    Min 12 characters ({form.description.length} written)
                  </span>
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => set("description", e.target.value)}
                  placeholder="Provide rich details about features, specifications, materials, warranty, and compatibility..."
                  rows={3}
                  className={cn(
                    "w-full rounded-lg border bg-white p-3 text-sm text-slate-900 transition-all placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amazon-orange/30 dark:bg-slate-900 dark:text-white dark:placeholder:text-slate-500",
                    errors.description
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                      : "border-slate-200 hover:border-slate-300 focus:border-amazon-orange dark:border-white/10 dark:hover:border-white/20"
                  )}
                />
                {errors.description && (
                  <p className="mt-1 text-xs text-red-500">{errors.description}</p>
                )}
              </div>
            </div>
          </div>
          )}

          {/* Step 2: Department & Categorization Hierarchy */}
          {currentStep === 2 && (
            <div className="space-y-4 animate-in fade-in duration-150">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 text-[10px] text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                  2
                </span>
                <span>Category &amp; Navigation Hierarchy</span>
              </div>

            <div className="rounded-xl border border-slate-200/90 bg-slate-50/60 p-4 dark:border-white/10 dark:bg-slate-800/40 sm:p-5">
              <div className="grid gap-4 sm:grid-cols-2">
                {/* 1. Main Category */}
                <div>
                  <div className="mb-1.5 flex items-center justify-between">
                    <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">
                      1. Main Department <span className="text-red-500">*</span>
                    </label>
                    <Link
                      href="/admin/categories"
                      target="_blank"
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-amazon-orange hover:underline"
                      title="Manage real categories on the backend categories page"
                    >
                      <span>Manage Categories</span>
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                  </div>
                  {isCustomMain ? (
                    <div className="flex items-center gap-2">
                      <input
                        value={customMainInput}
                        onChange={(e) => {
                          setCustomMainInput(e.target.value);
                          if (errors.mainCategory) {
                            setErrors((prev) => {
                              const next = { ...prev };
                              delete next.mainCategory;
                              return next;
                            });
                          }
                        }}
                        placeholder="e.g. Health & Wellness"
                        className="h-10 flex-1 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-amazon-orange focus:ring-2 focus:ring-amazon-orange/20 dark:border-white/10 dark:bg-slate-900"
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={cancelCustomMain}
                        title="Cancel custom category and return to list"
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 dark:border-white/10 dark:bg-slate-900 dark:text-slate-300"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <select
                      value={mainCategory}
                      onChange={(e) => handleMainCategoryChange(e.target.value)}
                      className={cn(
                        "h-10 w-full rounded-lg border bg-white px-3 text-sm outline-none transition focus:border-amazon-orange focus:ring-2 focus:ring-amazon-orange/20 dark:bg-slate-900",
                        errors.mainCategory
                          ? "border-red-500"
                          : "border-slate-200 dark:border-white/10"
                      )}
                    >
                      <option value="">-- Select Main Department --</option>
                      {backendMainCategories.length > 0 && (
                        <optgroup label="Store Categories (from Backend)">
                          {backendMainCategories.map((catName) => (
                            <option key={catName} value={catName}>
                              {catName}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {defaultMainCategories.length > 0 && (
                        <optgroup label="Other Standard Departments">
                          {defaultMainCategories.map((catName) => (
                            <option key={catName} value={catName}>
                              {catName}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {mainCategory &&
                        !backendMainCategories.includes(mainCategory) &&
                        !defaultMainCategories.includes(mainCategory) && (
                          <optgroup label="Current Product Department">
                            <option value={mainCategory}>{mainCategory}</option>
                          </optgroup>
                        )}
                      <option value="__custom__">➕ + Add Custom Department...</option>
                    </select>
                  )}
                  {errors.mainCategory && (
                    <p className="mt-1 text-xs text-red-500">{errors.mainCategory}</p>
                  )}
                </div>

                {/* 2. Sub Category */}
                <div>
                  <label className="mb-1.5 block text-xs font-bold text-slate-700 dark:text-slate-300">
                    2. Specific Subcategory <span className="text-red-500">*</span>
                  </label>
                  {isCustomSub ? (
                    <div className="flex items-center gap-2">
                      <input
                        value={customSubInput}
                        onChange={(e) => {
                          setCustomSubInput(e.target.value);
                          if (errors.subCategory) {
                            setErrors((prev) => {
                              const next = { ...prev };
                              delete next.subCategory;
                              return next;
                            });
                          }
                        }}
                        placeholder="e.g. Wireless Noise Canceling"
                        className="h-10 flex-1 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-amazon-orange focus:ring-2 focus:ring-amazon-orange/20 dark:border-white/10 dark:bg-slate-900"
                        autoFocus
                      />
                      {!isCustomMain && (
                        <button
                          type="button"
                          onClick={cancelCustomSub}
                          title="Cancel custom subcategory"
                          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 dark:border-white/10 dark:bg-slate-900 dark:text-slate-300"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ) : (
                    <select
                      value={subCategory}
                      disabled={!mainCategory}
                      onChange={(e) => handleSubCategoryChange(e.target.value)}
                      className={cn(
                        "h-10 w-full rounded-lg border bg-white px-3 text-sm outline-none transition focus:border-amazon-orange focus:ring-2 focus:ring-amazon-orange/20 disabled:opacity-50 disabled:bg-slate-100 dark:bg-slate-900 dark:disabled:bg-slate-800",
                        errors.subCategory
                          ? "border-red-500"
                          : "border-slate-200 dark:border-white/10"
                      )}
                    >
                      {!mainCategory ? (
                        <option value="">(Select a Main Department first)</option>
                      ) : (
                        <>
                          <option value="">-- Select Subcategory --</option>
                          {availableSubCategories.map((subName) => (
                            <option key={subName} value={subName}>
                              {subName}
                            </option>
                          ))}
                          <option value="__custom__">➕ + Add Custom Subcategory...</option>
                        </>
                      )}
                    </select>
                  )}
                  {errors.subCategory && (
                    <p className="mt-1 text-xs text-red-500">{errors.subCategory}</p>
                  )}
                </div>
              </div>

              {/* Breadcrumb Preview */}
              <div className="mt-3.5 flex flex-wrap items-center gap-1.5 rounded-xl border border-slate-200/80 bg-white px-3.5 py-2.5 text-xs text-slate-600 shadow-2xs dark:border-white/5 dark:bg-slate-900/80 dark:text-slate-300">
                <span className="font-bold text-slate-400 dark:text-slate-500 mr-1">
                  Breadcrumb Trail:
                </span>
                <span className="font-medium text-slate-600 dark:text-slate-400">Home</span>
                <ChevronRight className="h-3 w-3 text-slate-400" />
                <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 font-semibold text-slate-800 dark:bg-slate-800 dark:text-slate-200">
                  {(isCustomMain ? customMainInput : mainCategory) || "Department"}
                </span>
                <ChevronRight className="h-3 w-3 text-slate-400" />
                <span className="inline-flex items-center rounded-md bg-amazon-orange/15 px-2 py-0.5 font-semibold text-amber-900 dark:text-amber-200">
                  {(isCustomSub ? customSubInput : subCategory) || "Subcategory"}
                </span>
                <ChevronRight className="h-3 w-3 text-slate-400" />
                <span className="truncate max-w-[220px] text-slate-400 italic">
                  {form.title.trim() || "Product Title"}
                </span>
              </div>

              {/* Promotion Banner Bridge Link */}
              {Boolean((isCustomMain ? customMainInput : mainCategory)?.trim()) && (
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2.5 rounded-xl border border-amber-300/60 bg-gradient-to-r from-amber-50 to-orange-50/50 p-3 text-xs dark:border-amber-500/20 dark:from-amber-950/30 dark:to-orange-950/20">
                  <div className="flex items-center gap-2 overflow-hidden">
                    <Megaphone className="h-4 w-4 shrink-0 text-amazon-orange" />
                    <span className="font-bold text-amber-950 dark:text-amber-200 shrink-0">
                      Banner Target Link:
                    </span>
                    <code className="truncate rounded-md bg-white/90 px-2 py-0.5 font-mono text-[11px] font-semibold text-slate-800 shadow-2xs dark:bg-slate-900 dark:text-slate-200">
                      {getCategoryPermanentLink(
                        (isCustomMain ? customMainInput : mainCategory).trim()
                      )}
                    </code>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => {
                        const catName = (isCustomMain ? customMainInput : mainCategory).trim();
                        const link = getCategoryPermanentLink(catName);
                        navigator.clipboard.writeText(link);
                        toast.success(`Copied "${catName}" link for promotion banners!`);
                      }}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300/80 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 shadow-2xs hover:bg-slate-50 dark:border-white/10 dark:bg-slate-800 dark:text-slate-200"
                    >
                      <Copy className="h-3.5 w-3.5 text-amazon-orange" />
                      <span>Copy Link</span>
                    </button>
                    <a
                      href={`/admin/promotions?category=${encodeURIComponent(
                        (isCustomMain ? customMainInput : mainCategory).trim()
                      )}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-lg bg-amazon-orange px-2.5 py-1 text-xs font-bold text-slate-950 shadow-2xs hover:bg-[#e07320]"
                    >
                      <Megaphone className="h-3.5 w-3.5" />
                      <span>Create Banner</span>
                    </a>
                  </div>
                </div>
              )}

              {/* Cross-listing Secondary Tags */}
              <div className="mt-3.5 border-t border-slate-200/70 pt-3 dark:border-white/5">
                <button
                  type="button"
                  onClick={() => setShowExtraOptions((v) => !v)}
                  className="flex items-center gap-1.5 text-xs font-bold text-amazon-orange hover:underline"
                >
                  <Tag className="h-3.5 w-3.5" />
                  <span>
                    {showExtraOptions
                      ? "Hide secondary category tags"
                      : "+ Cross-list to additional category tags (optional)"}
                  </span>
                  {extraCategories.length > 0 && (
                    <span className="ml-1 rounded-full bg-amazon-orange/15 px-2 py-0.2 text-[10px] font-black text-amazon-orange">
                      {extraCategories.length}
                    </span>
                  )}
                </button>

                {showExtraOptions && (
                  <div className="mt-2.5 space-y-2">
                    <p className="text-[11px] text-slate-500">
                      Add optional secondary department tags (e.g. &quot;Deals&quot;, &quot;Featured&quot;, &quot;Gaming&quot;):
                    </p>
                    <div className="flex items-center gap-2">
                      <input
                        value={newExtraCategoryInput}
                        onChange={(e) => setNewExtraCategoryInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddExtraCategory();
                          }
                        }}
                        placeholder="Type extra category and press Enter"
                        className="h-8 max-w-xs flex-1 rounded-lg border border-slate-200 bg-white px-2.5 text-xs outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={handleAddExtraCategory}
                        className="h-8 gap-1 text-xs"
                      >
                        <Plus className="h-3 w-3" /> Add Tag
                      </Button>
                    </div>
                    {extraCategories.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {extraCategories.map((cat) => (
                          <span
                            key={cat}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-slate-200/80 px-2.5 py-1 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                          >
                            <span>{cat}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveExtraCategory(cat)}
                              className="text-slate-400 hover:text-red-500"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          )}

          {/* Step 3: Pricing & Inventory */}
          {currentStep === 3 && (
            <div className="space-y-4 animate-in fade-in duration-150">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 text-[10px] text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                  3
                </span>
                <span>Pricing &amp; Inventory</span>
              </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {/* Base Price */}
              <div>
                <label className="mb-1.5 block text-xs font-bold text-slate-700 dark:text-slate-300">
                  Base Price (ETB) <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">
                    ETB
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={form.price}
                    onChange={(e) => set("price", e.target.value)}
                    placeholder="29999"
                    className={cn(inputClass("price"), "pl-11 font-bold")}
                  />
                </div>
                {errors.price && <p className="mt-1 text-xs text-red-500">{errors.price}</p>}
              </div>

              {/* MRP */}
              <div>
                <label className="mb-1.5 flex items-center justify-between text-xs font-bold text-slate-700 dark:text-slate-300">
                  <span>List MRP (ETB)</span>
                  <span className="text-[10px] font-normal text-slate-400">Optional</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">
                    ETB
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={form.mrp}
                    onChange={(e) => set("mrp", e.target.value)}
                    placeholder="34999"
                    className={cn(inputClass("mrp"), "pl-11")}
                  />
                </div>
                {errors.mrp && <p className="mt-1 text-xs text-red-500">{errors.mrp}</p>}
              </div>

              {/* Discount */}
              <div>
                <label className="mb-1.5 flex items-center justify-between text-xs font-bold text-slate-700 dark:text-slate-300">
                  <span>Discount %</span>
                  <span className="text-[10px] font-normal text-slate-400">0 - 95%</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    max="95"
                    value={form.discount}
                    onChange={(e) => set("discount", e.target.value)}
                    className={inputClass("discount")}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">
                    %
                  </span>
                </div>
                {errors.discount && (
                  <p className="mt-1 text-xs text-red-500">{errors.discount}</p>
                )}
              </div>

              {/* Base Stock */}
              <div>
                <label className="mb-1.5 block text-xs font-bold text-slate-700 dark:text-slate-300">
                  Base Inventory Stock <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min="0"
                  value={form.stock}
                  onChange={(e) => set("stock", e.target.value)}
                  placeholder="50"
                  className={cn(
                    inputClass("stock"),
                    Number(form.stock) === 0 && "text-red-500 font-bold"
                  )}
                />
                {errors.stock && <p className="mt-1 text-xs text-red-500">{errors.stock}</p>}
              </div>
            </div>
          </div>
          )}

          {/* Step 4: Media & Gallery Assets */}
          {currentStep === 4 && (
            <div className="space-y-4 animate-in fade-in duration-150">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 text-[10px] text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                  4
                </span>
                <span>Media &amp; Gallery Assets</span>
              </div>

            <div className="grid gap-4 md:grid-cols-2">
              {/* Main Product Image */}
              <div className="rounded-xl border border-slate-200/90 bg-slate-50/50 p-4 dark:border-white/10 dark:bg-slate-800/40">
                <label className="mb-1.5 block text-xs font-bold text-slate-700 dark:text-slate-300">
                  Main Hero Cover Image URL
                </label>
                <div className="flex gap-3 items-center">
                  <div className="flex-1">
                    <input
                      type="url"
                      value={form.image}
                      onChange={(e) => {
                        set("image", e.target.value);
                        setImageError(false);
                      }}
                      placeholder="https://example.com/product-main.jpg"
                      className={inputClass("image")}
                    />
                    {errors.image && <p className="mt-1 text-xs text-red-500">{errors.image}</p>}
                  </div>
                  <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-white p-1 shadow-2xs dark:border-white/10 dark:bg-slate-900 flex items-center justify-center">
                    {form.image && !imageError ? (
                      <Image
                        src={form.image}
                        alt="Preview"
                        fill
                        className="object-contain p-1"
                        sizes="48px"
                        onError={() => setImageError(true)}
                      />
                    ) : (
                      <ImageOff className="h-5 w-5 text-slate-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Gallery Sub-URLs Add Bar */}
              <div className="rounded-xl border border-slate-200/90 bg-slate-50/50 p-4 dark:border-white/10 dark:bg-slate-800/40">
                <label className="mb-1.5 flex items-center justify-between text-xs font-bold text-slate-700 dark:text-slate-300">
                  <span>Add Gallery Angle URL</span>
                  <span className="text-[10px] font-normal text-slate-400">
                    {additionalImages.length} extra image(s)
                  </span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={newSubUrl}
                    onChange={(e) => setNewSubUrl(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddSubUrl();
                      }
                    }}
                    placeholder="https://example.com/gallery-angle.jpg"
                    className="h-10 flex-1 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-amazon-orange focus:ring-2 focus:ring-amazon-orange/20 dark:border-white/10 dark:bg-slate-900"
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleAddSubUrl}
                    className="h-10 gap-1 text-xs font-bold"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add
                  </Button>
                </div>
              </div>

              {/* Gallery Image List (Full width) */}
              {additionalImages.length > 0 && (
                <div className="md:col-span-2 rounded-xl border border-slate-200/80 bg-white p-4 shadow-2xs dark:border-white/10 dark:bg-slate-900">
                  <div className="mb-3 flex items-center justify-between text-xs font-semibold text-slate-500">
                    <span>Configured Gallery Assets ({additionalImages.length})</span>
                    <span>Reorder, edit inline, or remove</span>
                  </div>
                  <div className="space-y-2">
                    {additionalImages.map((url, idx) => {
                      const isEditing = editingSubUrlIdx === idx;
                      return (
                        <div
                          key={`${idx}-${url}`}
                          className={cn(
                            "flex flex-col sm:flex-row sm:items-center gap-2.5 rounded-xl border p-2.5 transition",
                            isEditing
                              ? "border-amazon-orange bg-amber-50/40 dark:border-amazon-orange dark:bg-amber-950/20"
                              : "border-slate-200/80 bg-slate-50/50 hover:border-slate-300 dark:border-white/10 dark:bg-slate-800/40"
                          )}
                        >
                          {/* Thumbnail */}
                          <div className="flex items-center gap-2.5 shrink-0">
                            <div className="relative h-11 w-11 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-white p-0.5 dark:border-white/10 dark:bg-slate-900 flex items-center justify-center">
                              {isEditing && editingSubUrlText.trim() ? (
                                <Image
                                  src={editingSubUrlText.trim()}
                                  alt=""
                                  fill
                                  sizes="44px"
                                  className="object-contain p-0.5"
                                />
                              ) : url ? (
                                <Image
                                  src={url}
                                  alt=""
                                  fill
                                  sizes="44px"
                                  className="object-contain p-0.5"
                                />
                              ) : (
                                <ImageOff className="h-4 w-4 text-slate-400" />
                              )}
                            </div>
                            <span className="rounded-md bg-slate-200/80 px-2 py-0.5 text-xs font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                              #{idx + 1}
                            </span>
                          </div>

                          {/* Content */}
                          {isEditing ? (
                            <div className="flex flex-1 flex-wrap items-center gap-2 min-w-0">
                              <input
                                type="url"
                                value={editingSubUrlText}
                                onChange={(e) => setEditingSubUrlText(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleSaveEditSubUrl(idx);
                                  } else if (e.key === "Escape") {
                                    handleCancelEditSubUrl();
                                  }
                                }}
                                autoFocus
                                className="h-8 flex-1 min-w-[200px] rounded-lg border border-amazon-orange bg-white px-2.5 text-xs outline-none dark:bg-slate-800"
                              />
                              <div className="flex items-center gap-1.5 shrink-0">
                                <button
                                  type="button"
                                  onClick={() => handleSaveEditSubUrl(idx)}
                                  className="inline-flex h-8 items-center gap-1 rounded-lg bg-emerald-600 px-2.5 text-xs font-bold text-white hover:bg-emerald-700"
                                >
                                  <Check className="h-3 w-3" /> Save
                                </button>
                                <button
                                  type="button"
                                  onClick={handleCancelEditSubUrl}
                                  className="inline-flex h-8 items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-600 hover:bg-slate-100 dark:border-white/10 dark:bg-slate-800 dark:text-slate-300"
                                >
                                  <X className="h-3 w-3" /> Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex-1 min-w-0">
                              <p
                                className="cursor-pointer font-mono text-xs text-slate-700 hover:text-amazon-orange dark:text-slate-300 break-all select-all"
                                title="Click to edit URL"
                                onClick={() => handleStartEditSubUrl(idx, url)}
                              >
                                {url}
                              </p>
                            </div>
                          )}

                          {/* Controls */}
                          {!isEditing && (
                            <div className="flex items-center gap-1 shrink-0 self-end sm:self-center">
                              <button
                                type="button"
                                disabled={idx === 0}
                                onClick={() => handleMoveSubUrl(idx, idx - 1)}
                                className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-30 dark:border-white/10 dark:bg-slate-800 dark:text-slate-300"
                                title="Move up"
                              >
                                <ChevronUp className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                disabled={idx === additionalImages.length - 1}
                                onClick={() => handleMoveSubUrl(idx, idx + 1)}
                                className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-30 dark:border-white/10 dark:bg-slate-800 dark:text-slate-300"
                                title="Move down"
                              >
                                <ChevronDown className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleStartEditSubUrl(idx, url)}
                                className="flex h-7 items-center gap-1 rounded-md border border-slate-200 bg-white px-2 text-xs font-medium text-slate-600 hover:border-amazon-orange hover:text-amazon-orange dark:border-white/10 dark:bg-slate-800 dark:text-slate-300"
                                title="Edit URL"
                              >
                                <Edit3 className="h-3 w-3" />
                                <span className="hidden sm:inline">Edit</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  navigator.clipboard.writeText(url);
                                  toast.success("URL copied to clipboard");
                                }}
                                className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-white/10 dark:bg-slate-800 dark:text-slate-300"
                                title="Copy URL"
                              >
                                <Copy className="h-3 w-3" />
                              </button>
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-white/10 dark:bg-slate-800 dark:text-slate-300"
                                title="Open in new tab"
                              >
                                <ExternalLink className="h-3 w-3" />
                              </a>
                              <button
                                type="button"
                                onClick={() => handleRemoveSubUrl(idx)}
                                className="flex h-7 w-7 items-center justify-center rounded-md border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-400"
                                title="Delete"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
          )}

          {/* Step 5: Multi-Color & Size Variants */}
          {currentStep === 5 && (
            <div className="space-y-4 animate-in fade-in duration-150">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 text-[10px] text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                  5
                </span>
                <span>Variants & Sizes System</span>
              </div>

            <div className="rounded-2xl border border-slate-200/90 bg-white shadow-2xs dark:border-white/10 dark:bg-slate-900 transition-all overflow-hidden">
              <div
                role="button"
                tabIndex={0}
                onClick={() => setIsVariantsOpen((prev) => !prev)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setIsVariantsOpen((prev) => !prev);
                  }
                }}
                className="flex flex-wrap items-center justify-between gap-3 p-4 sm:p-5 cursor-pointer select-none transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
              >
                <div className="space-y-1">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-50 text-amazon-orange dark:bg-amber-950/60">
                      <Palette className="h-4 w-4" />
                    </div>
                    <h3 className="text-sm font-bold text-slate-900 dark:text-white sm:text-base">
                      Product Variants & Sizes
                    </h3>
                    <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-bold text-amber-900 dark:bg-amber-950 dark:text-amber-200">
                      {variants.length} variant{variants.length === 1 ? "" : "s"}
                    </span>
                    {variants.length > 0 && !isVariantsOpen && (
                      <span className="hidden sm:inline-flex items-center text-xs text-slate-500 dark:text-slate-400">
                        • {variants.reduce((sum, v) => sum + (Number(v.stock) || 0), 0)} total stock
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {editing?.title || form.title || "Catalog item"} • Configure colors and sizes with custom photos, pricing, stock, and SKUs.
                  </p>

                  {/* Collapsed quick preview pills */}
                  {!isVariantsOpen && variants.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5 pt-1.5">
                      <span className="text-[11px] font-semibold text-slate-400">Configured:</span>
                      {variants.map((v) => (
                        <span
                          key={v.id}
                          className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-100/90 px-2.5 py-0.5 text-[11px] font-medium text-slate-700 dark:border-white/10 dark:bg-slate-800 dark:text-slate-300"
                        >
                          <span
                            className="h-2.5 w-2.5 rounded-full border border-black/20"
                            style={{ backgroundColor: v.colorCode || "#000000" }}
                          />
                          <span>
                            {v.color || "Unnamed"}
                            {v.size ? ` (${v.size})` : ""}
                          </span>
                          <span className="font-mono text-[10px] text-slate-400">
                            ({v.stock || 0})
                          </span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <div className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-bold text-slate-700 transition hover:border-amazon-orange hover:bg-amber-50 hover:text-amber-950 dark:border-white/10 dark:bg-slate-800 dark:text-slate-200">
                    <span>{isVariantsOpen ? "Collapse Variants" : "Manage Variants"}</span>
                    <ChevronDown
                      className={cn(
                        "h-4 w-4 transition-transform duration-200",
                        isVariantsOpen ? "rotate-180" : ""
                      )}
                    />
                  </div>
                </div>
              </div>

              {isVariantsOpen && (
                <div className="border-t border-slate-200 p-4 sm:p-5 pt-4 dark:border-white/10 space-y-4">
                  {/* Current Variants List */}
                  {variants.length > 0 ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between border-b border-slate-100 pb-2 dark:border-white/5">
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                          Configured Variants ({variants.length})
                        </span>
                        <button
                          type="button"
                          onClick={toggleAllVariantCards}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-amazon-orange hover:underline"
                        >
                          {variants.every((v) => Boolean(collapsedVariantCards[v.id])) ? (
                            <>
                              <ChevronDown className="h-3.5 w-3.5" />
                              <span>Expand all cards</span>
                            </>
                          ) : (
                            <>
                              <ChevronUp className="h-3.5 w-3.5" />
                              <span>Collapse all cards</span>
                            </>
                          )}
                        </button>
                      </div>

                      {variants.map((v) => {
                        const isCardCollapsed = Boolean(collapsedVariantCards[v.id]);
                        return (
                          <div
                            key={v.id}
                            className="rounded-xl border border-slate-200/90 bg-slate-50/70 p-3.5 shadow-2xs dark:border-white/10 dark:bg-slate-800/60 transition-all"
                          >
                            <div
                              className={cn(
                                "flex flex-wrap items-center justify-between gap-3",
                                !isCardCollapsed && "border-b border-slate-200/80 pb-3 dark:border-white/10"
                              )}
                            >
                              <div className="flex items-center gap-3">
                                <div className="relative flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-300 bg-white shadow-2xs dark:border-white/15 dark:bg-slate-900">
                                  {v.image ? (
                                    <Image
                                      src={v.image}
                                      alt={v.color}
                                      fill
                                      sizes="44px"
                                      className="object-contain p-0.5"
                                    />
                                  ) : (
                                    <div
                                      className="h-full w-full"
                                      style={{ backgroundColor: v.colorCode || "#000000" }}
                                    />
                                  )}
                                </div>

                                <div>
                                  <div className="flex flex-wrap items-center gap-2">
                                    <input
                                      type="text"
                                      value={v.color}
                                      onChange={(e) =>
                                        updateVariant(v.id, { color: e.target.value })
                                      }
                                      placeholder="Color name"
                                      className="h-7 rounded-md border border-transparent bg-transparent px-1.5 text-sm font-bold text-slate-900 hover:border-slate-300 focus:border-amazon-orange focus:bg-white dark:text-white dark:hover:border-white/20 dark:focus:bg-slate-900"
                                    />
                                    <input
                                      type="color"
                                      value={v.colorCode || "#000000"}
                                      onChange={(e) =>
                                        updateVariant(v.id, { colorCode: e.target.value })
                                      }
                                      title="Change color hex"
                                      className="h-5 w-5 cursor-pointer rounded border-0 bg-transparent p-0"
                                    />
                                    {v.size && (
                                      <span className="rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-900 dark:border-amber-700 dark:bg-amber-950/80 dark:text-amber-200">
                                        {v.size.includes(",") ? `Sizes: ${v.size}` : `Size: ${v.size}`}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1.5 px-1.5">
                                    <span className="text-[10px] font-mono text-slate-400">SKU:</span>
                                    <input
                                      type="text"
                                      value={v.sku}
                                      onChange={(e) =>
                                        updateVariant(v.id, {
                                          sku: e.target.value.toUpperCase(),
                                        })
                                      }
                                      placeholder="e.g. M47-BLK"
                                      className="h-5 w-32 rounded border border-transparent bg-transparent px-1 font-mono text-[11px] uppercase text-slate-600 hover:border-slate-300 focus:border-amazon-orange focus:bg-white dark:text-slate-400 dark:hover:border-white/20 dark:focus:bg-slate-900"
                                    />
                                  </div>
                                </div>
                              </div>

                              <div className="flex flex-wrap items-center gap-2">
                                {isCardCollapsed && (
                                  <div className="hidden sm:flex items-center gap-2 mr-1 rounded-md bg-white/90 px-2.5 py-1 text-xs border border-slate-200/80 dark:bg-slate-900/60 dark:border-white/10">
                                    {v.size && (
                                      <>
                                        <span className="font-bold text-amber-700 dark:text-amber-300">
                                          {v.size.includes(",") ? `Sizes: ${v.size}` : `Size: ${v.size}`}
                                        </span>
                                        <span className="text-slate-300 dark:text-slate-600">•</span>
                                      </>
                                    )}
                                    <span className="font-semibold text-slate-700 dark:text-slate-300">
                                      Stock: <span className="font-mono">{v.stock || "0"}</span>
                                    </span>
                                    <span className="text-slate-300 dark:text-slate-600">•</span>
                                    <span className="font-bold text-slate-900 dark:text-white">
                                      {v.price ? `${v.price} ETB` : "Base price"}
                                    </span>
                                  </div>
                                )}

                                {/* Hot Badge */}
                                <button
                                  type="button"
                                  onClick={() => updateVariant(v.id, { isHot: !v.isHot })}
                                  className={cn(
                                    "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold transition shadow-2xs",
                                    v.isHot
                                      ? "border border-red-300 bg-red-100 text-red-700 dark:border-red-800 dark:bg-red-950/80 dark:text-red-300"
                                      : "border border-slate-300 bg-slate-100 text-slate-500 hover:text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-400"
                                  )}
                                  title="Toggle Hot Deal badge"
                                >
                                  <Flame
                                    className={cn(
                                      "h-3 w-3",
                                      v.isHot ? "fill-red-600 text-red-600" : "text-slate-400"
                                    )}
                                  />
                                  <span>{v.isHot ? "Hot 🔥" : "Normal"}</span>
                                </button>

                                {/* Active Toggle */}
                                <button
                                  type="button"
                                  onClick={() => updateVariant(v.id, { isActive: !v.isActive })}
                                  className={cn(
                                    "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold transition shadow-2xs",
                                    v.isActive
                                      ? "border border-emerald-300 bg-emerald-100 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950/80 dark:text-emerald-300"
                                      : "border border-slate-300 bg-slate-200 text-slate-600 dark:border-white/10 dark:bg-white/10 dark:text-slate-400"
                                  )}
                                  title="Click to toggle active status"
                                >
                                  <span
                                    className={cn(
                                      "h-1.5 w-1.5 rounded-full",
                                      v.isActive ? "bg-emerald-500" : "bg-slate-400"
                                    )}
                                  />
                                  <span>{v.isActive ? "Active" : "Inactive"}</span>
                                </button>

                                {/* Remove */}
                                <button
                                  type="button"
                                  onClick={() => removeVariant(v.id)}
                                  className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-bold text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/40"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                  <span>Remove</span>
                                </button>

                                {/* Card Expand/Collapse */}
                                <button
                                  type="button"
                                  onClick={() => toggleVariantCard(v.id)}
                                  className="inline-flex items-center justify-center h-7 w-7 rounded-lg border border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-800 dark:border-white/10 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 transition"
                                  title={
                                    isCardCollapsed ? "Expand variant inputs" : "Collapse variant inputs"
                                  }
                                >
                                  <ChevronDown
                                    className={cn(
                                      "h-4 w-4 transition-transform duration-200",
                                      !isCardCollapsed ? "rotate-180" : ""
                                    )}
                                  />
                                </button>
                              </div>
                            </div>

                            {/* Inputs Grid */}
                            {!isCardCollapsed && (
                              <div className="mt-3 space-y-3">
                                {/* Size configuration row with Checkboxes */}
                                {(() => {
                                  const cardSizes = v.size
                                    ? v.size
                                        .split(/[,/|]+/)
                                        .map((s) => s.trim())
                                        .filter(Boolean)
                                    : [];
                                  return (
                                    <div className="rounded-lg border border-slate-200/80 bg-white/80 p-2.5 dark:border-white/10 dark:bg-slate-900/60">
                                      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                        <div className="flex items-center gap-2">
                                          <label className="text-xs font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                                            <span>Available Sizes</span>
                                            <span className="text-[10px] font-normal text-slate-400">
                                              (Select multiple with checkboxes)
                                            </span>
                                          </label>
                                          {cardSizes.length > 0 && (
                                            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-900 dark:bg-amber-950 dark:text-amber-200">
                                              {cardSizes.length} selected ({cardSizes.join(", ")})
                                            </span>
                                          )}
                                        </div>
                                        {cardSizes.length > 0 && (
                                          <button
                                            type="button"
                                            onClick={() => updateVariant(v.id, { size: "" })}
                                            className="text-[11px] font-semibold text-slate-400 hover:text-red-500"
                                          >
                                            Clear all
                                          </button>
                                        )}
                                      </div>
                                      <div className="flex flex-wrap items-center gap-1.5">
                                        {SIZE_PRESETS.map((sz) => {
                                          const isChecked = cardSizes.includes(sz);
                                          return (
                                            <label
                                              key={sz}
                                              className={cn(
                                                "inline-flex items-center gap-1.5 cursor-pointer rounded-lg border px-2.5 py-1 text-xs font-bold transition shadow-2xs select-none",
                                                isChecked
                                                  ? "border-amazon-orange bg-amber-50 text-amazon-orange dark:border-amber-600 dark:bg-amber-950/60"
                                                  : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50 dark:border-white/10 dark:bg-slate-800 dark:text-slate-300"
                                              )}
                                            >
                                              <input
                                                type="checkbox"
                                                checked={isChecked}
                                                onChange={(e) => {
                                                  const next = e.target.checked
                                                    ? [...cardSizes, sz]
                                                    : cardSizes.filter((s) => s !== sz);
                                                  updateVariant(v.id, { size: next.join(", ") });
                                                }}
                                                className="h-3.5 w-3.5 rounded border-slate-300 text-amazon-orange accent-amazon-orange focus:ring-amazon-orange"
                                              />
                                              <span>{sz}</span>
                                            </label>
                                          );
                                        })}
                                        <div className="flex items-center gap-1.5 ml-1">
                                          <span className="text-[11px] text-slate-400">or Custom:</span>
                                          <input
                                            type="text"
                                            value={
                                              cardSizes
                                                .filter((s) => !SIZE_PRESETS.includes(s))
                                                .join(", ")
                                            }
                                            onChange={(e) => {
                                              const customVals = e.target.value
                                                .split(/[,/]+/)
                                                .map((s) => s.trim())
                                                .filter(Boolean);
                                              const presetVals = cardSizes.filter((s) =>
                                                SIZE_PRESETS.includes(s)
                                              );
                                              const combined = [...presetVals, ...customVals];
                                              updateVariant(v.id, { size: combined.join(", ") });
                                            }}
                                            placeholder="e.g. 42, 34W"
                                            className="h-7 w-24 rounded border border-slate-200 bg-white px-2 text-xs font-semibold uppercase outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })()}

                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                  <div>
                                    <label className="mb-1 block text-xs font-bold text-slate-700 dark:text-slate-300">
                                      Stock quantity
                                    </label>
                                    <input
                                      type="number"
                                      min="0"
                                      value={v.stock}
                                      onChange={(e) =>
                                        updateVariant(v.id, { stock: e.target.value })
                                      }
                                      placeholder={form.stock || "20"}
                                      className="h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-xs outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
                                    />
                                  </div>

                                  <div>
                                    <label className="mb-1 block text-xs font-bold text-slate-700 dark:text-slate-300">
                                      Price (ETB)
                                    </label>
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0.01"
                                      value={v.price}
                                      onChange={(e) =>
                                        updateVariant(v.id, { price: e.target.value })
                                      }
                                      placeholder={form.price || "29999"}
                                      className="h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-bold outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
                                    />
                                  </div>

                                  <div className="sm:col-span-2 lg:col-span-1">
                                    <label className="mb-1 block text-xs font-bold text-slate-700 dark:text-slate-300">
                                      Variant Image URL
                                    </label>
                                    <div className="flex gap-2">
                                      <input
                                        type="url"
                                        value={v.image}
                                        onChange={(e) =>
                                          updateVariant(v.id, { image: e.target.value })
                                        }
                                        placeholder="https://.../variant.jpg"
                                        className="h-9 flex-1 rounded-lg border border-slate-200 bg-white px-2.5 text-xs outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
                                      />
                                      <div className="relative h-9 w-9 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-white p-0.5 dark:border-white/10 dark:bg-slate-800 flex items-center justify-center">
                                        {v.image ? (
                                          <Image
                                            src={v.image}
                                            alt=""
                                            fill
                                            sizes="36px"
                                            className="object-contain p-0.5"
                                          />
                                        ) : (
                                          <ImageOff className="h-4 w-4 text-slate-400" />
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50/50 p-6 text-center dark:border-white/15 dark:bg-slate-900/40">
                      <Palette className="mx-auto h-8 w-8 text-slate-400" />
                      <p className="mt-2 text-sm font-bold text-slate-700 dark:text-slate-300">
                        No variants added yet
                      </p>
                      <p className="mt-0.5 text-xs text-slate-500">
                        Use the color & size builder below to configure variants with distinct stock, sizes, and pricing.
                      </p>
                    </div>
                  )}

                  {/* Add a Variant Card */}
                  <div className="rounded-xl border border-slate-200/90 bg-slate-50/80 p-4 dark:border-white/10 dark:bg-slate-800/50 space-y-3">
                    <div>
                      <h4 className="text-sm font-bold text-slate-900 dark:text-white">
                        Add Product Variant (Color & Size)
                      </h4>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Select a color, then optionally pick one or multiple sizes (e.g. S, M, L, XL) to generate per-size stock.
                      </p>
                    </div>

                    {/* Presets */}
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-xs font-semibold text-slate-500">Color Presets:</span>
                      {COLOR_PRESETS.map((preset) => (
                        <button
                          key={preset.name}
                          type="button"
                          onClick={() => {
                            setNewVarColor(preset.name);
                            setNewVarColorCode(preset.code);
                            const baseSlug = form.slug || slugify(form.title) || "prod";
                            const sizeSuffix =
                              newVarSize.trim() ? `-${newVarSize.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-")}` : "";
                            setNewVarSku(
                              `${baseSlug}-${preset.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}${sizeSuffix}`.toUpperCase()
                            );
                          }}
                          className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:border-amazon-orange hover:bg-amber-50/50 dark:border-white/10 dark:bg-slate-800 dark:text-slate-200"
                        >
                          <span
                            className="inline-block h-2.5 w-2.5 rounded-full border border-black/20"
                            style={{ backgroundColor: preset.code }}
                          />
                          <span>+ {preset.name}</span>
                        </button>
                      ))}
                    </div>

                    {/* Size selector & batch generation */}
                    <div className="rounded-lg border border-slate-200/80 bg-white p-3 dark:border-white/10 dark:bg-slate-900/60">
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-xs font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                          <span>Sizes for this color:</span>
                          <span className="text-[11px] font-normal text-slate-500 dark:text-slate-400">
                            (Select multiple sizes to automatically generate all size variants!)
                          </span>
                        </label>
                        {selectedBatchSizes.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setSelectedBatchSizes([])}
                            className="text-[11px] font-semibold text-red-500 hover:underline"
                          >
                            Clear selected sizes ({selectedBatchSizes.length})
                          </button>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {SIZE_PRESETS.map((sz) => {
                          const isSelected = selectedBatchSizes.includes(sz);
                          return (
                            <label
                              key={sz}
                              className={cn(
                                "inline-flex items-center gap-1.5 cursor-pointer rounded-lg border px-2.5 py-1 text-xs font-bold transition shadow-2xs select-none",
                                isSelected
                                  ? "border-amazon-orange bg-amber-50 text-amazon-orange dark:border-amber-600 dark:bg-amber-950/60"
                                  : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50 dark:border-white/10 dark:bg-slate-900 dark:text-slate-300"
                              )}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  setSelectedBatchSizes((prev) =>
                                    e.target.checked ? [...prev, sz] : prev.filter((s) => s !== sz)
                                  );
                                  setNewVarSize("");
                                }}
                                className="h-3.5 w-3.5 rounded border-slate-300 text-amazon-orange accent-amazon-orange focus:ring-amazon-orange"
                              />
                              <span>{sz}</span>
                            </label>
                          );
                        })}
                        <div className="flex items-center gap-1.5 ml-2">
                          <span className="text-xs text-slate-400">or Custom Size:</span>
                          <input
                            type="text"
                            value={newVarSize}
                            onChange={(e) => {
                              setNewVarSize(e.target.value);
                              if (e.target.value) setSelectedBatchSizes([]);
                            }}
                            placeholder="e.g. 42 or XL"
                            className="h-8 w-28 rounded-lg border border-slate-200 bg-white px-2 text-xs font-semibold uppercase outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      <div>
                        <label className="mb-1 block text-xs font-bold text-slate-700 dark:text-slate-300">
                          Color name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={newVarColor}
                          onChange={(e) => {
                            const val = e.target.value;
                            setNewVarColor(val);
                            if (!newVarSku && val) {
                              const baseSlug = form.slug || slugify(form.title) || "prod";
                              const sizeSuffix =
                                newVarSize.trim() ? `-${newVarSize.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-")}` : "";
                              setNewVarSku(
                                `${baseSlug}-${val.toLowerCase().replace(/[^a-z0-9]+/g, "-")}${sizeSuffix}`.toUpperCase()
                              );
                            }
                          }}
                          placeholder="e.g. Black"
                          className="h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-xs outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
                        />
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-bold text-slate-700 dark:text-slate-300">
                          Color swatch
                        </label>
                        <div className="flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-2 dark:border-white/10 dark:bg-slate-900">
                          <input
                            type="color"
                            value={newVarColorCode}
                            onChange={(e) => setNewVarColorCode(e.target.value)}
                            className="h-6 w-6 cursor-pointer rounded border-0 bg-transparent p-0"
                          />
                          <input
                            type="text"
                            value={newVarColorCode}
                            onChange={(e) => setNewVarColorCode(e.target.value)}
                            placeholder="#1a1a1a"
                            className="w-full bg-transparent font-mono text-xs uppercase outline-none"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-bold text-slate-700 dark:text-slate-300">
                          Stock quantity per variant
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={newVarStock}
                          onChange={(e) => setNewVarStock(e.target.value)}
                          placeholder={form.stock || "20"}
                          className="h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-xs outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
                        />
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-bold text-slate-700 dark:text-slate-300">
                          Price (ETB)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0.01"
                          value={newVarPrice}
                          onChange={(e) => setNewVarPrice(e.target.value)}
                          placeholder={form.price || "Base price"}
                          className="h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-xs outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label className="mb-1 block text-xs font-bold text-slate-700 dark:text-slate-300">
                          SKU Code (Base / Pattern)
                        </label>
                        <input
                          type="text"
                          value={newVarSku}
                          onChange={(e) => setNewVarSku(e.target.value.toUpperCase())}
                          placeholder="e.g. TSHIRT-BLK"
                          className="h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 font-mono text-xs uppercase outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label className="mb-1 block text-xs font-bold text-slate-700 dark:text-slate-300">
                          Product Image (optional)
                        </label>
                        <input
                          type="url"
                          value={newVarImage}
                          onChange={(e) => setNewVarImage(e.target.value)}
                          placeholder="https://.../variant.jpg (defaults to main image)"
                          className="h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-xs outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end pt-1">
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleAddVariant}
                        className="h-9 gap-1.5 bg-amazon-orange text-xs font-bold text-slate-950 hover:bg-[#e07320]"
                      >
                        <Plus className="h-4 w-4" />
                        {selectedBatchSizes.length > 1
                          ? `Add ${selectedBatchSizes.length} Variants (${selectedBatchSizes.join(", ")})`
                          : selectedBatchSizes.length === 1 || newVarSize
                          ? `Add Variant with Size "${selectedBatchSizes[0] || newVarSize}"`
                          : "Add Variant"}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          )}

          {/* Step 6: Storefront Merchandising & Placement Controls */}
          {currentStep === 6 && (
            <div className="space-y-4 animate-in fade-in duration-150">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 text-[10px] text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                  6
                </span>
                <span>Storefront Merchandising &amp; Placement</span>
              </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
              {/* 1. Promotion Banner */}
              <label
                className={cn(
                  "flex items-start gap-3 rounded-xl border p-3.5 cursor-pointer transition shadow-2xs",
                  form.inPromotionBanner
                    ? "border-amber-300 bg-amber-50/70 dark:border-amber-500/40 dark:bg-amber-950/20"
                    : "border-slate-200 bg-white hover:border-slate-300 dark:border-white/10 dark:bg-slate-900"
                )}
              >
                <input
                  type="checkbox"
                  checked={form.inPromotionBanner}
                  onChange={(e) => set("inPromotionBanner", e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-amazon-orange rounded"
                />
                <div>
                  <span className="block text-xs font-bold text-amber-950 dark:text-amber-200">
                    🎉 Hero Banner
                  </span>
                  <span className="text-[11px] text-slate-500 dark:text-slate-400">
                    Showcase in homepage
                  </span>
                </div>
              </label>

              {/* 3. Featured */}
              <label
                className={cn(
                  "flex items-start gap-3 rounded-xl border p-3.5 cursor-pointer transition shadow-2xs",
                  form.isFeatured
                    ? "border-blue-300 bg-blue-50/70 dark:border-blue-500/40 dark:bg-blue-950/20"
                    : "border-slate-200 bg-white hover:border-slate-300 dark:border-white/10 dark:bg-slate-900"
                )}
              >
                <input
                  type="checkbox"
                  checked={form.isFeatured}
                  onChange={(e) => set("isFeatured", e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-blue-600 rounded"
                />
                <div>
                  <span className="block text-xs font-bold text-slate-900 dark:text-white">
                    ⭐ Featured
                  </span>
                  <span className="text-[11px] text-slate-500 dark:text-slate-400">
                    Curated grid items
                  </span>
                </div>
              </label>

              {/* 4. Deals */}
              <label
                className={cn(
                  "flex items-start gap-3 rounded-xl border p-3.5 cursor-pointer transition shadow-2xs",
                  form.isFlashDeal
                    ? "border-rose-300 bg-rose-50/70 dark:border-rose-500/40 dark:bg-rose-950/20"
                    : "border-slate-200 bg-white hover:border-slate-300 dark:border-white/10 dark:bg-slate-900"
                )}
              >
                <input
                  type="checkbox"
                  checked={form.isFlashDeal}
                  onChange={(e) => set("isFlashDeal", e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-red-600 rounded"
                />
                <div>
                  <span className="block text-xs font-bold text-slate-900 dark:text-white">
                    ⚡ Today&apos;s Deal
                  </span>
                  <span className="text-[11px] text-slate-500 dark:text-slate-400">
                    Flash &amp; lightning deal
                  </span>
                </div>
              </label>

              {/* 5. Prime */}
              <label
                className={cn(
                  "flex items-start gap-3 rounded-xl border p-3.5 cursor-pointer transition shadow-2xs",
                  form.isPrime
                    ? "border-teal-300 bg-teal-50/70 dark:border-teal-500/40 dark:bg-teal-950/20"
                    : "border-slate-200 bg-white hover:border-slate-300 dark:border-white/10 dark:bg-slate-900"
                )}
              >
                <input
                  type="checkbox"
                  checked={form.isPrime}
                  onChange={(e) => set("isPrime", e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-amazon-teal rounded"
                />
                <div>
                  <span className="block text-xs font-bold text-slate-900 dark:text-white">
                    📦 Prime Badge
                  </span>
                  <span className="text-[11px] text-slate-500 dark:text-slate-400">
                    Fast free delivery
                  </span>
                </div>
              </label>

              {/* 6. Sponsored */}
              <label
                className={cn(
                  "flex items-start gap-3 rounded-xl border p-3.5 cursor-pointer transition shadow-2xs",
                  form.isSponsored
                    ? "border-purple-300 bg-purple-50/70 dark:border-purple-500/40 dark:bg-purple-950/20"
                    : "border-slate-200 bg-white hover:border-slate-300 dark:border-white/10 dark:bg-slate-900"
                )}
              >
                <input
                  type="checkbox"
                  checked={form.isSponsored}
                  onChange={(e) => set("isSponsored", e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-purple-600 rounded"
                />
                <div>
                  <span className="block text-xs font-bold text-slate-900 dark:text-white">
                    📢 Sponsored
                  </span>
                  <span className="text-[11px] text-slate-500 dark:text-slate-400">
                    Sponsored promotion
                  </span>
                </div>
              </label>
            </div>
          </div>
          )}
        </div>

        {/* Form Bottom Actions Toolbar with Step Navigation */}
        <div className="flex flex-col gap-3 border-t border-slate-100 bg-slate-50/50 px-5 py-4 dark:border-white/5 dark:bg-slate-800/30 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-slate-500 dark:text-slate-400">
              Step {currentStep} of {FORM_STEPS.length}:
            </span>
            <span className="text-xs font-black text-slate-900 dark:text-white">
              {FORM_STEPS[currentStep - 1]?.label}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            {currentStep > 1 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handlePrevStep}
                className="h-9 gap-1.5 text-xs font-bold"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
            )}

            {currentStep < FORM_STEPS.length ? (
              <Button
                type="button"
                size="sm"
                onClick={handleNextStep}
                className="h-9 gap-1.5 bg-slate-900 text-xs font-bold text-white hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-100"
              >
                <span>Continue to {FORM_STEPS[currentStep]?.shortLabel}</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
            ) : null}

            {editing ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={cancelEdit}
                className="h-9 text-xs font-semibold"
              >
                Cancel Edit
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsFormOpen(false)}
                className="h-9 text-xs font-semibold"
              >
                Close Form
              </Button>
            )}

            <Button
              type="submit"
              size="sm"
              disabled={loading}
              className="gap-2 bg-amazon-orange text-xs font-bold text-slate-950 hover:bg-[#e07320]"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {editing ? "Update Product" : "Create New Product"}
            </Button>
          </div>
        </div>
      </form>
      )}

      {/* 4. Unified Search & Command Bar */}
      <div className="rounded-2xl border border-slate-200/90 bg-white p-4 shadow-xs dark:border-white/10 dark:bg-slate-900">
        {/* Status Review Filter Tabs */}
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 pb-3.5 mb-3.5 dark:border-white/5 w-full">
          <span className="text-xs font-bold text-slate-500 dark:text-slate-400 mr-1">Status:</span>
          {[
            { id: "All", label: "All Products", count: products.length },
            { id: "PENDING", label: "Pending Approval", count: pendingCount, highlight: pendingCount > 0 },
            { id: "APPROVED", label: "Approved (Public)", count: approvedCount },
            { id: "REJECTED", label: "Rejected", count: rejectedCount },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => {
                setFilterStatus(tab.id);
                setPage(1);
              }}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-bold transition shadow-2xs",
                filterStatus === tab.id
                  ? tab.id === "PENDING"
                    ? "bg-amber-500 text-white shadow-xs"
                    : tab.id === "REJECTED"
                    ? "bg-red-600 text-white shadow-xs"
                    : "bg-[#073B70] text-white shadow-xs"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10"
              )}
            >
              <span>{tab.label}</span>
              <span
                className={cn(
                  "rounded-full px-1.5 py-0.2 text-[10px] font-black",
                  filterStatus === tab.id
                    ? "bg-white/20 text-white"
                    : tab.highlight
                    ? "bg-amber-500 text-white animate-pulse"
                    : "bg-slate-200 text-slate-700 dark:bg-white/10 dark:text-slate-300"
                )}
              >
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Search Box */}
          <div className="relative min-w-[260px] flex-1">
            <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search products by title, brand, slug…"
              className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50/50 pl-10 pr-9 text-sm text-slate-900 outline-none transition focus:border-amazon-orange focus:bg-white focus:ring-2 focus:ring-amazon-orange/20 dark:border-white/10 dark:bg-slate-800 dark:text-white dark:focus:bg-slate-900"
            />
            {search && (
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setPage(1);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 dark:hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Category Select */}
            <select
              value={filterCategory}
              onChange={(e) => {
                setFilterCategory(e.target.value);
                setPage(1);
              }}
              className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 outline-none transition hover:border-slate-300 focus:border-amazon-orange focus:ring-2 focus:ring-amazon-orange/20 dark:border-white/10 dark:bg-slate-800 dark:text-slate-200"
            >
              <option value="All">All Categories ({allFilterCategories.length})</option>
              {allFilterCategories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>

            {/* Placement Filter */}
            <select
              value={filterPlacement}
              onChange={(e) => {
                setFilterPlacement(e.target.value);
                setPage(1);
              }}
              className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 outline-none transition hover:border-slate-300 focus:border-amazon-orange focus:ring-2 focus:ring-amazon-orange/20 dark:border-white/10 dark:bg-slate-800 dark:text-slate-200"
            >
              <option value="All">All Placements</option>
              <option value="Banner">🎉 Hero Banner ({stats.inBanner})</option>
              <option value="Featured">⭐ Featured</option>
              <option value="Deals">⚡ Deals</option>
              <option value="Sponsored">📢 Sponsored</option>
            </select>

            {isAnyFilterActive && (
              <button
                type="button"
                onClick={clearAllFilters}
                className="inline-flex h-10 items-center gap-1.5 rounded-xl border border-slate-200 px-3 text-xs font-bold text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:border-white/10 dark:text-slate-300 dark:hover:bg-slate-800 transition"
              >
                <X className="h-3.5 w-3.5" />
                <span>Reset</span>
              </button>
            )}
          </div>
        </div>

        {/* Results Counter / Filter Pills */}
        <div className="mt-3 flex flex-wrap items-center justify-between border-t border-slate-100 pt-3 text-xs text-slate-500 dark:border-white/5 dark:text-slate-400">
          <div className="flex items-center gap-2">
            <span>
              Showing <strong className="text-slate-900 dark:text-white">{filtered.length}</strong> of{" "}
              {products.length} catalog items
            </span>
            {isAnyFilterActive && (
              <span className="rounded-full bg-amazon-orange/15 px-2 py-0.5 text-[10px] font-bold text-amazon-orange">
                Filtered view
              </span>
            )}
          </div>
          {filtered.length > perPage && (
            <span>
              Page {page} of {Math.ceil(filtered.length / perPage)}
            </span>
          )}
        </div>
      </div>

      {/* 4. Products Table */}
      <div className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-xs dark:border-white/10 dark:bg-slate-900">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[840px] text-left text-sm">
            <thead className="border-b border-slate-200/80 bg-slate-50/80 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:border-white/10 dark:bg-slate-800/60 dark:text-slate-400">
              <tr>
                <th className="px-5 py-3.5">Product & Seller</th>
                <th className="px-4 py-3.5">Category</th>
                <th className="px-4 py-3.5">Variants</th>
                <th className="px-4 py-3.5">Price</th>
                <th className="px-4 py-3.5">Stock</th>
                <th className="px-4 py-3.5">Hero Banner</th>
                <th className="px-4 py-3.5">Merchandising</th>
                <th className="px-5 py-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-white/5">
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-16 text-center">
                    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500">
                      <Package className="h-7 w-7" />
                    </div>
                    <p className="mt-3 text-base font-bold text-slate-900 dark:text-white">
                      {products.length === 0
                        ? "No products in the database yet"
                        : "No matching products found"}
                    </p>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      {products.length === 0
                        ? "Click Create New Product below to add your very first catalog product listing."
                        : "Try adjusting your search query, department, or status filters."}
                    </p>
                    {products.length === 0 ? (
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleToggleCreate}
                        className="mt-4 h-9 gap-1.5 bg-amazon-orange text-xs font-bold text-slate-950 hover:bg-[#e07320]"
                      >
                        <Plus className="h-4 w-4 stroke-[2.5]" />
                        Create New Product
                      </Button>
                    ) : isAnyFilterActive ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={clearAllFilters}
                        className="mt-4 h-8 text-xs font-semibold"
                      >
                        Clear Filters
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ) : (
                paginated.map((product) => {
                  const productVariants = product.variants || [];
                  const variantPrices = productVariants.map((v) => v.price);
                  const minPrice =
                    variantPrices.length > 0 ? Math.min(...variantPrices) : product.price;
                  const maxPrice =
                    variantPrices.length > 0 ? Math.max(...variantPrices) : product.price;

                  return (
                    <tr
                      key={product.id}
                      className="group transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
                    >
                      {/* Product Thumbnail, Title & Vendor Attribution */}
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <div
                            className="relative h-12 w-12 shrink-0 overflow-hidden rounded-xl border border-slate-200/90 bg-white p-1 shadow-2xs transition-all duration-150 group-hover/thumb:border-[#087DB5] group-hover/thumb:shadow-md dark:border-white/10 dark:bg-slate-900 flex items-center justify-center cursor-pointer"
                            title={product.title}
                          >
                            {product.images?.[0] ? (
                              <Image
                                src={product.images[0]}
                                alt={product.title}
                                fill
                                sizes="48px"
                                className="object-contain p-0.5 transition-transform duration-150 group-hover/thumb:scale-105"
                              />
                            ) : (
                              <ImageOff className="h-5 w-5 text-slate-400" />
                            )}
                          </div>
                          <div className="min-w-0 max-w-xs">
                            <p className="font-bold text-slate-900 dark:text-white line-clamp-1 text-xs sm:text-sm" title={product.title}>
                              {product.title}
                            </p>
                            <div className="flex flex-wrap items-center gap-1.5 mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                              <span className="font-medium">{product.brand || "Brand"}</span>
                              {product.seller && product.seller !== "MOVA Market Fulfilment" && (
                                <>
                                  <span>·</span>
                                  <span className="inline-flex items-center gap-1 font-semibold text-[#087DB5] dark:text-[#38bdf8]">
                                    <Store className="h-3 w-3" />
                                    {product.seller}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Category Hierarchy */}
                      <td className="px-4 py-3.5">
                        {product.categories && product.categories.length >= 2 ? (
                          <div className="flex flex-col gap-0.5">
                            <span className="text-xs font-bold text-slate-900 dark:text-white">
                              {product.categories[0]}
                            </span>
                            <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
                              ↳ {product.categories[1]}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                            {product.category || "Unassigned"}
                          </span>
                        )}
                      </td>

                      {/* Variants Swatches */}
                      <td className="px-4 py-3.5">
                        {productVariants.length > 0 ? (
                          <div className="flex items-center gap-2">
                            <div className="flex -space-x-1.5 overflow-hidden py-0.5">
                              {productVariants.slice(0, 4).map((v) => (
                                <span
                                  key={v.id}
                                  title={`${v.color} - ${formatPrice(v.price)}`}
                                  className="inline-block h-5 w-5 rounded-full border-2 border-white shadow-2xs dark:border-slate-900 shrink-0"
                                  style={{ backgroundColor: v.colorCode || "#000000" }}
                                />
                              ))}
                            </div>
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                              {productVariants.length} color{productVariants.length > 1 ? "s" : ""}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs font-medium text-slate-400">Standard</span>
                        )}
                      </td>

                      {/* Price */}
                      <td className="px-4 py-3.5 font-bold text-slate-900 dark:text-white">
                        {minPrice !== maxPrice ? (
                          <div>
                            <span className="text-[11px] font-normal text-slate-500">From </span>
                            <span>{formatPrice(minPrice)}</span>
                          </div>
                        ) : (
                          formatPrice(product.price)
                        )}
                      </td>

                      {/* Stock */}
                      <td className="px-4 py-3.5">
                        {product.stock === 0 ? (
                          <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-black text-red-700 dark:bg-red-950/80 dark:text-red-300">
                            Out of stock
                          </span>
                        ) : product.stock <= 10 ? (
                          <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-bold text-amber-800 dark:bg-amber-950/80 dark:text-amber-300">
                            {product.stock} low
                          </span>
                        ) : (
                          <span className="font-semibold text-slate-800 dark:text-slate-200">
                            {product.stock}
                          </span>
                        )}
                      </td>

                      {/* Hero Banner 1-Click Toggle */}
                      <td className="px-4 py-3.5">
                        <button
                          type="button"
                          onClick={() => togglePromotionBanner(product)}
                          disabled={updatingBannerId === product.id}
                          className={cn(
                            "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold transition shadow-2xs",
                            product.inPromotionBanner
                              ? "bg-amber-100 text-amber-900 ring-1 ring-amber-400/50 hover:bg-amber-200 dark:bg-amber-950/60 dark:text-amber-200 dark:ring-amber-500/30"
                              : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-white/10 dark:text-slate-400"
                          )}
                          title={
                            product.inPromotionBanner
                              ? "Click to remove from hero promotion banner"
                              : "Click to display in homepage hero promotion banner"
                          }
                        >
                          {updatingBannerId === product.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Megaphone
                              className={cn(
                                "h-3.5 w-3.5",
                                product.inPromotionBanner
                                  ? "text-amber-600 dark:text-amber-400"
                                  : "text-slate-400"
                              )}
                            />
                          )}
                          <span>{product.inPromotionBanner ? "In Banner" : "Off"}</span>
                        </button>
                      </td>

                      {/* Merchandising Badges */}
                      <td className="px-4 py-3.5">
                        <div className="flex flex-col gap-1.5">
                          {/* Review / Approval Status */}
                          {product.status === "PENDING" || product.status === "PENDING_REVIEW" ? (
                            <span className="inline-flex w-fit items-center gap-1 rounded-md border border-amber-300 bg-amber-100 px-1.5 py-0.5 text-[10px] font-black text-amber-900 dark:border-amber-600/40 dark:bg-amber-950/80 dark:text-amber-200">
                              <Clock className="h-2.5 w-2.5 text-amber-700 dark:text-amber-400" />
                              Pending Review
                            </span>
                          ) : product.status === "REJECTED" ? (
                            <span
                              className="inline-flex w-fit items-center gap-1 rounded-md border border-red-300 bg-red-100 px-1.5 py-0.5 text-[10px] font-black text-red-900 dark:border-red-600/40 dark:bg-red-950/80 dark:text-red-200 cursor-help"
                              title={`Rejection Reason: ${product.rejectionReason || "Listing needs revision"}`}
                            >
                              <XCircle className="h-2.5 w-2.5 text-red-700 dark:text-red-400" />
                              Rejected
                            </span>
                          ) : (
                            <span className="inline-flex w-fit items-center gap-1 rounded-md border border-emerald-300 bg-emerald-100 px-1.5 py-0.5 text-[10px] font-black text-emerald-900 dark:border-emerald-600/40 dark:bg-emerald-950/80 dark:text-emerald-200">
                              <Check className="h-2.5 w-2.5 text-emerald-700 dark:text-emerald-400" />
                              Approved
                            </span>
                          )}

                          {product.sellerPhone && (
                            <div className="flex items-center gap-1 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300" title={`Seller contact: ${product.sellerPhone}`}>
                              <Phone className="h-3 w-3 shrink-0" />
                              <span className="truncate max-w-[120px]">{product.sellerPhone}</span>
                            </div>
                          )}

                          <div className="flex flex-wrap gap-1">
                            {product.inPromotionBanner && (
                              <span className="rounded-md bg-amber-100 px-1.5 py-0.5 text-[10px] font-black text-amber-900 dark:bg-amber-950 dark:text-amber-200">
                                Banner
                              </span>
                            )}
                            {product.isFeatured && (
                              <Badge tone="success" className="text-[10px] py-0.5">
                                Featured
                              </Badge>
                            )}
                            {product.isFlashDeal && (
                              <Badge tone="deal" className="text-[10px] py-0.5">
                                ⚡ Deal
                              </Badge>
                            )}
                            {product.isPrime && (
                              <Badge tone="prime" className="text-[10px] py-0.5">
                                Prime
                              </Badge>
                            )}
                            {product.isSponsored && (
                              <span className="rounded-md bg-purple-100 px-1.5 py-0.5 text-[10px] font-bold text-purple-900 dark:bg-purple-950 dark:text-purple-200">
                                Sponsored
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-end gap-1.5">
                          {/* Quick Approve / Reject for Pending Listings */}
                          {(product.status === "PENDING" || product.status === "PENDING_REVIEW") && (
                            <>
                              <button
                                type="button"
                                onClick={() => handleApprove(product)}
                                disabled={processingStatusId === product.id}
                                className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-2.5 py-1.5 text-xs font-bold text-white shadow-2xs hover:bg-emerald-700 transition active:scale-95 disabled:opacity-50"
                                title="Approve Listing"
                              >
                                <Check className="h-3.5 w-3.5" />
                                <span>Approve</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setRejectModalProduct(product);
                                  setRejectReason("");
                                }}
                                disabled={processingStatusId === product.id}
                                className="inline-flex items-center gap-1 rounded-lg bg-red-600 px-2.5 py-1.5 text-xs font-bold text-white shadow-2xs hover:bg-red-700 transition active:scale-95 disabled:opacity-50"
                                title="Reject Listing with Reason"
                              >
                                <X className="h-3.5 w-3.5" />
                                <span>Reject</span>
                              </button>
                            </>
                          )}

                          {product.status === "REJECTED" && (
                            <button
                              type="button"
                              onClick={() => handleApprove(product)}
                              disabled={processingStatusId === product.id}
                              className="inline-flex items-center gap-1 rounded-lg border border-emerald-300 bg-emerald-50 px-2 py-1.5 text-xs font-bold text-emerald-700 hover:bg-emerald-100 transition disabled:opacity-50"
                              title="Re-approve Listing"
                            >
                              <Check className="h-3.5 w-3.5" />
                              <span>Approve</span>
                            </button>
                          )}

                          <a
                            href={`/products/${product.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 shadow-2xs hover:border-[#087DB5] hover:text-[#087DB5] dark:border-white/10 dark:bg-slate-800 dark:text-slate-300 transition"
                            title="Preview Listing Page"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>

                          <button
                            type="button"
                            onClick={() => openVariantModal(product)}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 shadow-2xs hover:border-amazon-orange hover:text-amazon-orange dark:border-white/10 dark:bg-slate-800 dark:text-slate-300 transition"
                            title="Manage Color Variants"
                          >
                            <Palette className="h-3.5 w-3.5 text-amazon-orange" />
                            <span className="hidden sm:inline">Variants</span>
                            <span className="rounded-full bg-slate-100 px-1.5 py-0.2 text-[10px] font-bold text-slate-800 dark:bg-white/15 dark:text-white">
                              {product.variants?.length || 0}
                            </span>
                          </button>

                          <button
                            type="button"
                            onClick={() => startEdit(product)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 shadow-2xs hover:border-amazon-orange hover:text-amazon-orange dark:border-white/10 dark:bg-slate-800 dark:text-slate-300 transition"
                            title="Edit Product"
                          >
                            <Edit3 className="h-3.5 w-3.5" />
                          </button>

                          {confirmDeleteId === product.id ? (
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => deleteProduct(product.id)}
                                className="rounded-lg bg-red-600 px-2.5 py-1 text-xs font-bold text-white shadow-2xs hover:bg-red-700"
                              >
                                Confirm
                              </button>
                              <button
                                type="button"
                                onClick={() => setConfirmDeleteId(null)}
                                className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 dark:border-white/10 dark:bg-slate-800 dark:text-slate-300"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setConfirmDeleteId(product.id)}
                              className="flex h-8 w-8 items-center justify-center rounded-lg border border-red-200/80 bg-red-50/60 text-red-600 hover:bg-red-100 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-400 transition"
                              title="Delete Listing"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 5. Pagination */}
      <AdminPagination
        currentPage={page}
        totalItems={filtered.length}
        perPage={perPage}
        onPageChange={setPage}
        onPerPageChange={(newPerPage) => {
          setPerPage(newPerPage);
          setPage(1);
        }}
        itemLabel="products"
      />

      {/* 6. Quick Color Variants Modal */}
      {variantModalProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs">
          <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-white/10 dark:bg-slate-900 overflow-hidden animate-fade-in">
            {/* Modal Header */}
            <div className="flex items-start justify-between border-b border-slate-200 p-5 dark:border-white/10 bg-slate-50/50 dark:bg-slate-800/40">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amazon-orange dark:bg-amber-950/60">
                  <Palette className="h-5 w-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-bold text-slate-950 dark:text-white">
                      Manage Product Variants (Colors & Sizes)
                    </h3>
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-900 dark:bg-amber-950 dark:text-amber-200">
                      {modalVariants.length} variant{modalVariants.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    {variantModalProduct.title} • Base: {formatPrice(variantModalProduct.price)} • Stock:{" "}
                    {variantModalProduct.stock}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setVariantModalProduct(null)}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/10 dark:hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {modalVariants.length > 0 ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-2 dark:border-white/5">
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Configured Variants ({modalVariants.length})
                    </span>
                    <button
                      type="button"
                      onClick={toggleAllModalCards}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-amazon-orange hover:underline"
                    >
                      {modalVariants.every((v) => Boolean(modalCollapsedCards[v.id])) ? (
                        <>
                          <ChevronDown className="h-3.5 w-3.5" />
                          <span>Expand all cards</span>
                        </>
                      ) : (
                        <>
                          <ChevronUp className="h-3.5 w-3.5" />
                          <span>Collapse all cards</span>
                        </>
                      )}
                    </button>
                  </div>

                  {modalVariants.map((v) => {
                    const isCardCollapsed = Boolean(modalCollapsedCards[v.id]);
                    return (
                      <div
                        key={v.id}
                        className="rounded-xl border border-slate-200 bg-slate-50/70 p-3.5 shadow-2xs dark:border-white/10 dark:bg-slate-800/60 transition-all"
                      >
                        <div
                          className={cn(
                            "flex flex-wrap items-center justify-between gap-3",
                            !isCardCollapsed && "border-b border-slate-200/80 pb-3 dark:border-white/10"
                          )}
                        >
                          <div className="flex items-center gap-3">
                            <div className="relative flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-300 bg-white shadow-2xs dark:border-white/15 dark:bg-slate-900">
                              {v.image ? (
                                <Image
                                  src={v.image}
                                  alt={v.color}
                                  fill
                                  sizes="44px"
                                  className="object-contain p-0.5"
                                />
                              ) : (
                                <div
                                  className="h-full w-full"
                                  style={{ backgroundColor: v.colorCode || "#000000" }}
                                />
                              )}
                            </div>

                            <div>
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={v.color}
                                  onChange={(e) =>
                                    updateModalVariant(v.id, { color: e.target.value })
                                  }
                                  placeholder="Color name"
                                  className="h-7 rounded-md border border-transparent bg-transparent px-1.5 text-sm font-bold text-slate-900 hover:border-slate-300 focus:border-amazon-orange focus:bg-white dark:text-white dark:hover:border-white/20 dark:focus:bg-slate-900"
                                />
                                <input
                                  type="color"
                                  value={v.colorCode || "#000000"}
                                  onChange={(e) =>
                                    updateModalVariant(v.id, { colorCode: e.target.value })
                                  }
                                  title="Change color hex"
                                  className="h-5 w-5 cursor-pointer rounded border-0 bg-transparent p-0"
                                />
                                {v.size && (
                                  <span className="rounded-md border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[11px] font-bold text-indigo-700 dark:border-indigo-800 dark:bg-indigo-950/70 dark:text-indigo-300">
                                    {v.size.includes(",") ? `Sizes: ${v.size}` : `Size: ${v.size}`}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-1.5 px-1.5">
                                <span className="text-[10px] font-mono text-slate-400">SKU:</span>
                                <input
                                  type="text"
                                  value={v.sku}
                                  onChange={(e) =>
                                    updateModalVariant(v.id, {
                                      sku: e.target.value.toUpperCase(),
                                    })
                                  }
                                  placeholder="e.g. M47-BLK"
                                  className="h-5 w-32 rounded border border-transparent bg-transparent px-1 font-mono text-[11px] uppercase text-slate-600 hover:border-slate-300 focus:border-amazon-orange focus:bg-white dark:text-slate-400 dark:hover:border-white/20 dark:focus:bg-slate-900"
                                />
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-wrap items-center gap-2">
                            {isCardCollapsed && (
                              <div className="hidden sm:flex items-center gap-2 mr-1 rounded-md bg-white/90 px-2.5 py-1 text-xs border border-slate-200/80 dark:bg-slate-900/60 dark:border-white/10">
                                {v.size && (
                                  <>
                                    <span className="font-bold text-indigo-600 dark:text-indigo-400">
                                      {v.size.includes(",") ? `Sizes: ${v.size}` : `Size: ${v.size}`}
                                    </span>
                                    <span className="text-slate-300 dark:text-slate-600">•</span>
                                  </>
                                )}
                                <span className="font-semibold text-slate-700 dark:text-slate-300">
                                  Stock: <span className="font-mono">{v.stock || "0"}</span>
                                </span>
                                <span className="text-slate-300 dark:text-slate-600">•</span>
                                <span className="font-bold text-slate-900 dark:text-white">
                                  {v.price ? `${v.price} ETB` : "Base price"}
                                </span>
                              </div>
                            )}

                            {/* Hot Badge */}
                            <button
                              type="button"
                              onClick={() => updateModalVariant(v.id, { isHot: !v.isHot })}
                              className={cn(
                                "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold transition shadow-2xs",
                                v.isHot
                                  ? "border border-red-300 bg-red-100 text-red-700 dark:border-red-800 dark:bg-red-950/80 dark:text-red-300"
                                  : "border border-slate-300 bg-slate-100 text-slate-500 hover:text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-400"
                              )}
                              title="Toggle Hot Deal badge"
                            >
                              <Flame
                                className={cn(
                                  "h-3 w-3",
                                  v.isHot ? "fill-red-600 text-red-600" : "text-slate-400"
                                )}
                              />
                              <span>{v.isHot ? "Hot 🔥" : "Normal"}</span>
                            </button>

                            {/* Active Toggle */}
                            <button
                              type="button"
                              onClick={() => updateModalVariant(v.id, { isActive: !v.isActive })}
                              className={cn(
                                "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold transition shadow-2xs",
                                v.isActive
                                  ? "border border-emerald-300 bg-emerald-100 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950/80 dark:text-emerald-300"
                                  : "border border-slate-300 bg-slate-200 text-slate-600 dark:border-white/10 dark:bg-white/10 dark:text-slate-400"
                              )}
                              title="Click to toggle active status"
                            >
                              <span
                                className={cn(
                                  "h-1.5 w-1.5 rounded-full",
                                  v.isActive ? "bg-emerald-500" : "bg-slate-400"
                                )}
                              />
                              <span>{v.isActive ? "Active" : "Inactive"}</span>
                            </button>

                            {/* Remove */}
                            <button
                              type="button"
                              onClick={() => removeModalVariant(v.id)}
                              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-bold text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/40"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              <span>Remove</span>
                            </button>

                            {/* Card Expand/Collapse */}
                            <button
                              type="button"
                              onClick={() => toggleModalCard(v.id)}
                              className="inline-flex items-center justify-center h-7 w-7 rounded-lg border border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-800 dark:border-white/10 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 transition"
                              title={
                                isCardCollapsed ? "Expand variant inputs" : "Collapse variant inputs"
                              }
                            >
                              <ChevronDown
                                className={cn(
                                  "h-4 w-4 transition-transform duration-200",
                                  !isCardCollapsed ? "rotate-180" : ""
                                )}
                              />
                            </button>
                          </div>
                        </div>

                        {/* Inputs Grid */}
                        {!isCardCollapsed && (
                          <div className="mt-3 space-y-3">
                            {/* Size Row with Checkboxes */}
                            {(() => {
                              const modalSizes = v.size
                                ? v.size
                                    .split(/[,/|]+/)
                                    .map((s) => s.trim())
                                    .filter(Boolean)
                                : [];
                              return (
                                <div className="rounded-lg border border-slate-200/80 bg-white/80 p-2.5 dark:border-white/10 dark:bg-slate-900/50">
                                  <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                                    <div className="flex items-center gap-2">
                                      <label className="text-xs font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                                        <span>Available Sizes</span>
                                        <span className="text-[10px] font-normal text-slate-400">
                                          (Select with checkboxes)
                                        </span>
                                      </label>
                                      {modalSizes.length > 0 && (
                                        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-900 dark:bg-amber-950 dark:text-amber-200">
                                          {modalSizes.length} selected ({modalSizes.join(", ")})
                                        </span>
                                      )}
                                    </div>
                                    {modalSizes.length > 0 && (
                                      <button
                                        type="button"
                                        onClick={() => updateModalVariant(v.id, { size: undefined })}
                                        className="text-[11px] font-medium text-slate-400 hover:text-red-500"
                                      >
                                        Clear all
                                      </button>
                                    )}
                                  </div>
                                  <div className="flex flex-wrap items-center gap-1.5">
                                    {SIZE_PRESETS.map((preset) => {
                                      const isChecked = modalSizes.includes(preset);
                                      return (
                                        <label
                                          key={preset}
                                          className={cn(
                                            "inline-flex items-center gap-1.5 cursor-pointer rounded-lg border px-2.5 py-1 text-xs font-bold transition shadow-2xs select-none",
                                            isChecked
                                              ? "border-amazon-orange bg-amber-50 text-amazon-orange dark:border-amber-600 dark:bg-amber-950/60"
                                              : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50 dark:border-white/10 dark:bg-slate-800 dark:text-slate-300"
                                          )}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={(e) => {
                                              const next = e.target.checked
                                                ? [...modalSizes, preset]
                                                : modalSizes.filter((s) => s !== preset);
                                              updateModalVariant(v.id, { size: next.join(", ") });
                                            }}
                                            className="h-3.5 w-3.5 rounded border-slate-300 text-amazon-orange accent-amazon-orange focus:ring-amazon-orange"
                                          />
                                          <span>{preset}</span>
                                        </label>
                                      );
                                    })}
                                    <div className="flex items-center gap-1.5 ml-1">
                                      <span className="text-[11px] text-slate-400">or Custom:</span>
                                      <input
                                        type="text"
                                        value={
                                          modalSizes
                                            .filter((s) => !SIZE_PRESETS.includes(s))
                                            .join(", ")
                                        }
                                        onChange={(e) => {
                                          const customVals = e.target.value
                                            .split(/[,/]+/)
                                            .map((s) => s.trim())
                                            .filter(Boolean);
                                          const presetVals = modalSizes.filter((s) =>
                                            SIZE_PRESETS.includes(s)
                                          );
                                          const combined = [...presetVals, ...customVals];
                                          updateModalVariant(v.id, { size: combined.join(", ") });
                                        }}
                                        placeholder="Custom size"
                                        className="h-7 w-24 rounded border border-slate-200 bg-white px-2 text-xs font-semibold uppercase outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
                                      />
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                              <div>
                                <label className="mb-1 block text-xs font-bold text-slate-700 dark:text-slate-300">
                                  Stock quantity
                                </label>
                                <input
                                  type="number"
                                  min="0"
                                  value={v.stock}
                                  onChange={(e) =>
                                    updateModalVariant(v.id, { stock: e.target.value })
                                  }
                                  placeholder="20"
                                  className="h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-xs outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
                                />
                              </div>

                              <div>
                                <label className="mb-1 block text-xs font-bold text-slate-700 dark:text-slate-300">
                                  Price (ETB)
                                </label>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0.01"
                                  value={v.price}
                                  onChange={(e) =>
                                    updateModalVariant(v.id, { price: e.target.value })
                                  }
                                  placeholder={String(variantModalProduct.price)}
                                  className="h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-bold outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
                                />
                              </div>

                              <div className="sm:col-span-2 lg:col-span-1">
                                <label className="mb-1 block text-xs font-bold text-slate-700 dark:text-slate-300">
                                  Variant Image URL
                                </label>
                                <div className="flex gap-2">
                                  <input
                                    type="url"
                                    value={v.image}
                                    onChange={(e) =>
                                      updateModalVariant(v.id, { image: e.target.value })
                                    }
                                    placeholder="https://.../variant.jpg"
                                    className="h-9 flex-1 rounded-lg border border-slate-200 bg-white px-2.5 text-xs outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
                                  />
                                  <div className="relative h-9 w-9 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-white p-0.5 dark:border-white/10 dark:bg-slate-800 flex items-center justify-center">
                                    {v.image ? (
                                      <Image
                                        src={v.image}
                                        alt=""
                                        fill
                                        sizes="36px"
                                        className="object-contain p-0.5"
                                      />
                                    ) : (
                                      <ImageOff className="h-4 w-4 text-slate-400" />
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50/50 p-6 text-center dark:border-white/15 dark:bg-slate-900/40">
                  <Palette className="mx-auto h-8 w-8 text-slate-400" />
                  <p className="mt-2 text-sm font-bold text-slate-700 dark:text-slate-300">
                    No product variants added yet
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    Add variants below with color and size (e.g. S, M, L, XL for clothing) to allow customers to choose their fit with custom pricing, individual stock, and photos.
                  </p>
                </div>
              )}

              {/* Add a Variant Card */}
              <div className="rounded-xl border border-slate-200/90 bg-slate-50/80 p-4 dark:border-white/10 dark:bg-slate-800/50">
                <h4 className="text-sm font-bold text-slate-900 dark:text-white">
                  Add a Variant (Color &amp; Sizes)
                </h4>

                <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                  <span className="text-xs font-semibold text-slate-500">Quick Colors:</span>
                  {COLOR_PRESETS.map((preset) => (
                    <button
                      key={preset.name}
                      type="button"
                      onClick={() => {
                        setModalNewColor(preset.name);
                        setModalNewColorCode(preset.code);
                        const sizeSuffix = modalNewSize.trim() ? `-${modalNewSize.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-")}` : "";
                        setModalNewSku(
                          `${variantModalProduct.slug}-${preset.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}${sizeSuffix}`.toUpperCase()
                        );
                      }}
                      className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:border-amazon-orange hover:bg-amber-50/50 dark:border-white/10 dark:bg-slate-800 dark:text-slate-200"
                    >
                      <span
                        className="inline-block h-2.5 w-2.5 rounded-full border border-black/20"
                        style={{ backgroundColor: preset.code }}
                      />
                      <span>+ {preset.name}</span>
                    </button>
                  ))}
                </div>

                {/* Size Selector in Modal */}
                <div className="mt-3 rounded-lg border border-slate-200/80 bg-white/70 p-2.5 dark:border-white/10 dark:bg-slate-900/40">
                  <div className="flex flex-wrap items-center justify-between gap-1.5 mb-1.5">
                    <span className="text-xs font-bold text-slate-700 dark:text-slate-300">
                      Sizes (Single or Multi-select Batch):
                    </span>
                    {modalSelectedBatchSizes.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setModalSelectedBatchSizes([])}
                        className="text-[11px] font-semibold text-red-500 hover:underline"
                      >
                        Clear selected sizes ({modalSelectedBatchSizes.length})
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {SIZE_PRESETS.map((sz) => {
                      const isSelected = modalSelectedBatchSizes.includes(sz);
                      return (
                        <label
                          key={sz}
                          className={cn(
                            "inline-flex items-center gap-1.5 cursor-pointer rounded-lg border px-2.5 py-1 text-xs font-bold transition shadow-2xs select-none",
                            isSelected
                              ? "border-amazon-orange bg-amber-50 text-amazon-orange dark:border-amber-600 dark:bg-amber-950/60"
                              : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50 dark:border-white/10 dark:bg-slate-800 dark:text-slate-300"
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              setModalSelectedBatchSizes((prev) =>
                                e.target.checked ? [...prev, sz] : prev.filter((s) => s !== sz)
                              );
                              setModalNewSize("");
                            }}
                            className="h-3.5 w-3.5 rounded border-slate-300 text-amazon-orange accent-amazon-orange focus:ring-amazon-orange"
                          />
                          <span>{sz}</span>
                        </label>
                      );
                    })}
                    <div className="flex items-center gap-1.5 ml-1">
                      <span className="text-xs text-slate-400">or Custom:</span>
                      <input
                        type="text"
                        value={modalNewSize}
                        onChange={(e) => {
                          setModalNewSize(e.target.value);
                          if (e.target.value) setModalSelectedBatchSizes([]);
                        }}
                        placeholder="e.g. XL"
                        className="h-7 w-20 rounded-md border border-slate-200 bg-white px-1.5 text-xs font-semibold uppercase outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div>
                    <label className="mb-1 block text-xs font-bold text-slate-700 dark:text-slate-300">
                      Color name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={modalNewColor}
                      onChange={(e) => {
                        const val = e.target.value;
                        setModalNewColor(val);
                        if (!modalNewSku && val) {
                          const sizeSuffix = modalNewSize.trim() ? `-${modalNewSize.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-")}` : "";
                          setModalNewSku(
                            `${variantModalProduct.slug}-${val.toLowerCase().replace(/[^a-z0-9]+/g, "-")}${sizeSuffix}`.toUpperCase()
                          );
                        }
                      }}
                      placeholder="e.g. Blue"
                      className="h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-xs outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-bold text-slate-700 dark:text-slate-300">
                      Color swatch
                    </label>
                    <div className="flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-2 dark:border-white/10 dark:bg-slate-900">
                      <input
                        type="color"
                        value={modalNewColorCode}
                        onChange={(e) => setModalNewColorCode(e.target.value)}
                        className="h-6 w-6 cursor-pointer rounded border-0 bg-transparent p-0"
                      />
                      <input
                        type="text"
                        value={modalNewColorCode}
                        onChange={(e) => setModalNewColorCode(e.target.value)}
                        placeholder="#1a1a1a"
                        className="w-full bg-transparent font-mono text-xs uppercase outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-bold text-slate-700 dark:text-slate-300">
                      Stock quantity
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={modalNewStock}
                      onChange={(e) => setModalNewStock(e.target.value)}
                      placeholder={String(variantModalProduct.stock || "20")}
                      className="h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-xs outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-bold text-slate-700 dark:text-slate-300">
                      Price (ETB)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={modalNewPrice}
                      onChange={(e) => setModalNewPrice(e.target.value)}
                      placeholder={String(variantModalProduct.price)}
                      className="h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-bold outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="mb-1 block text-xs font-bold text-slate-700 dark:text-slate-300">
                      SKU Code
                    </label>
                    <input
                      type="text"
                      value={modalNewSku}
                      onChange={(e) => setModalNewSku(e.target.value.toUpperCase())}
                      placeholder="e.g. M47-BLU"
                      className="h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 font-mono text-xs uppercase outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="mb-1 block text-xs font-bold text-slate-700 dark:text-slate-300">
                      Product image (optional)
                    </label>
                    <input
                      type="url"
                      value={modalNewImage}
                      onChange={(e) => setModalNewImage(e.target.value)}
                      placeholder="https://.../variant.jpg (defaults to main product image)"
                      className="h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-xs outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
                    />
                  </div>
                </div>

                <div className="mt-3.5 flex justify-end">
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleModalAddVariant}
                    className="h-9 gap-1.5 bg-amazon-orange text-xs font-bold text-slate-950 hover:bg-[#e07320]"
                  >
                    <Plus className="h-4 w-4" />
                    {modalSelectedBatchSizes.length > 1
                      ? `Add ${modalSelectedBatchSizes.length} Variants (${modalSelectedBatchSizes.join(", ")})`
                      : modalSelectedBatchSizes.length === 1 || modalNewSize
                      ? `Add Variant with Size "${modalSelectedBatchSizes[0] || modalNewSize}"`
                      : "Add Variant"}
                  </Button>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 border-t border-slate-200 bg-slate-50/50 p-4 dark:border-white/10 dark:bg-slate-800/40">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setVariantModalProduct(null)}
                disabled={savingModalVariants}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={saveModalVariants}
                disabled={savingModalVariants}
                className="gap-1.5 bg-amazon-orange text-xs font-bold text-slate-950 hover:bg-[#e07320]"
              >
                {savingModalVariants ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                Save Variants ({modalVariants.length})
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 7. Rejection Reason Modal */}
      {rejectModalProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-white/10 dark:bg-slate-900 animate-fade-in">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-100 text-red-600 dark:bg-red-950/60 dark:text-red-400">
                <AlertCircle className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-bold text-slate-900 dark:text-white">Reject Marketplace Listing</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{rejectModalProduct.title}</p>
              </div>
            </div>
            <p className="mt-4 text-xs text-slate-600 dark:text-slate-300">
              Please provide a clear reason for the seller. They will see this feedback in their seller dashboard and be allowed to edit and resubmit their listing for review.
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Please provide a clearer photo of the device screen, or clarify the model and warranty condition."
              rows={4}
              className="mt-3 w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-900 placeholder:text-slate-400 focus:border-red-500 focus:outline-none dark:border-white/10 dark:bg-slate-800 dark:text-white"
            />
            <div className="mt-5 flex items-center justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setRejectModalProduct(null);
                  setRejectReason("");
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                className="bg-red-600 text-white hover:bg-red-700 text-xs font-bold"
                disabled={processingStatusId === rejectModalProduct.id || !rejectReason.trim()}
                onClick={handleReject}
              >
                {processingStatusId === rejectModalProduct.id ? "Rejecting..." : "Confirm Rejection"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
